import { useState, useContext, useMemo, useCallback, useEffect, useRef, memo } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  Menu,
  TableSortLabel,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Box,
  Chip,
  Grid,
  Tooltip,
  Collapse,
  IconButton,
  InputAdornment,
  CircularProgress,
  LinearProgress,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  TablePagination,
  Autocomplete,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import ClearIcon from '@mui/icons-material/Clear';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import SearchIcon from '@mui/icons-material/Search';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import LastPageIcon from '@mui/icons-material/LastPage';
import PlaylistRemoveIcon from '@mui/icons-material/PlaylistRemove';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import EditableSelect from './EditableDisplayElements/EditableSelect';
import EditablePassword from './EditableDisplayElements/EditablePassword';
import EditableText from './EditableDisplayElements/EditableText';
import EditableBoolean from './EditableDisplayElements/EditableBoolean';
import EditablePrice from './EditableDisplayElements/EditablePrice';
import AddItemDialog from './DialogElements/AddItemDialog';
import EditableDetailView from './EditableDetailView';
import AuthContext from '../../context/authContext';
import useEditableList from '../../hooks/useEditableList';
import 'dayjs/locale/de';
import PasswordDialog from './DialogElements/PasswordDialog';
import { alpha, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import LockIconWrapper from '../components/LockIconWrapper';
import { toast } from 'react-toastify';
import { viewRootTextPrimarySx } from '../../util/chartTheme';

// Shared stack of EditableList instance ids that currently have a nested mobile
// view open. Lets the native back button close only the deepest open level.
let mobileNestedBackStack = [];

function EditableList({
  fieldSchema = {},
  data = null,
  itemId = null,
  schema = {},
  path = null,
  title = 'Editable List',
  nested = false,
  resourceType = 'resource',
  parentIsEditable = null,
  parentDataItem = null,
  onItemAdd = null,
  onItemUpdate = null,
  onItemDelete = null,
  options = {},
  nestedBasePath = null,
  renderRowActions = null,
  leadingRowAction = null, // (item) => { icon: ReactNode, tooltip?: string, onClick: (item) => void, disabled?: boolean, right?: string }
  hideRowActionsColumn = false,
  visibleColumnKeys = null,
  listParams = {},
  searchPlaceholder = 'Suchen...',
  initialSearchTerm = '',
  hideSearchField = false,
  hideColumnSelector = false,
  renderTopControlsStart = null,
  renderTopControlsEnd = null,
  onRowClick = null,
  selection = null,
  externalPagination = null, // { page, limit, total, onChange, rowsPerPageOptions? }
  getRowSx = null, // (item, index) => sx — merged on top of the default row sx; useful for highlighting rows (e.g. mail bounces)
}) {
  const authCtx = useContext(AuthContext);
  // Virtual column key for Mongo _id; column is available in selector but hidden by default
  const ID_COLUMN_KEY = '_id';
  const hideMongoIdColumn = options?.hideMongoIdColumn === true;
  const inlineEditingDisabled = options?.disableInlineEditing === true;
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [expandedItems, setExpandedItems] = useState({});
  const searchInputRef = useRef(null);
  const [currentSearchTerm, setCurrentSearchTerm] = useState(() => initialSearchTerm || '');
  // Initialize from listParams so the very first fetch already carries them,
  // instead of fetching once with {} and again after the sync effect below runs.
  const [extraParams, setExtraParams] = useState(() => listParams || {});
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [columnFilters, setColumnFilters] = useState({});
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [filterMenu, setFilterMenu] = useState({
    anchorEl: null,
    key: null,
    draftOp: null,
    draftValue: '',
  });
  const filterValueInputRef = useRef(null);
  const [columnMenuAnchorEl, setColumnMenuAnchorEl] = useState(null);
  const serverSideSortingWanted = !nested && options?.serverSideSorting !== false;
  const serverSideFilteringWanted = !nested && options?.serverSideFiltering !== false;
  // Sync external list params into internal state
  useEffect(() => {
    const nextParams = listParams || {};
    setExtraParams(prev =>
      JSON.stringify(prev) === JSON.stringify(nextParams) ? prev : nextParams
    );
    const nextLimit = Number(listParams?.limit);
    if (Number.isFinite(nextLimit) && nextLimit > 0) {
      setItemsPerPage(prev => (prev === nextLimit ? prev : nextLimit));
    }
  }, [JSON.stringify(listParams)]);
  // Last submitted search term — shown as the field's placeholder after Enter.
  const [activeSearchTerm, setActiveSearchTerm] = useState('');
  const [items, setItems] = useState(null);
  const [apiOptions, setApiOptions] = useState({});
  const [selectedItem, setSelectedItem] = useState(null);
  const [isDetailViewOpen, setIsDetailViewOpen] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState({
    open: false,
    itemId: null,
    itemName: null,
  });
  const [passwordDialog, setPasswordDialog] = useState({
    open: false,
    itemId: null,
  });
  const [emptyNestedDialog, setEmptyNestedDialog] = useState({
    open: false,
    parentId: null,
    path: null,
    schema: null,
  });
  const [visibleColumns, setVisibleColumns] = useState(
    Array.isArray(visibleColumnKeys) && visibleColumnKeys.length > 0
      ? visibleColumnKeys
      : Object.keys(schema)
  );

  // Keep visible columns in sync when caller provides a new initial set
  useEffect(() => {
    if (Array.isArray(visibleColumnKeys) && visibleColumnKeys.length > 0) {
      setVisibleColumns(visibleColumnKeys);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(visibleColumnKeys)]);

  useEffect(() => {
    if (!hideMongoIdColumn) return;
    setVisibleColumns(prev => {
      if (!Array.isArray(prev) || !prev.includes(ID_COLUMN_KEY)) return prev;
      return prev.filter(k => k !== ID_COLUMN_KEY);
    });
  }, [hideMongoIdColumn]);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(() => {
    const n = Number(listParams?.limit);
    return Number.isFinite(n) && n > 0 ? n : 20;
  });

  const selectionEnabled = Boolean(selection?.enabled);
  const selectionSelectedIds = Array.isArray(selection?.selectedIds) ? selection.selectedIds : [];
  const setSelectionSelectedIds =
    typeof selection?.onSelectedIdsChange === 'function' ? selection.onSelectedIdsChange : null;

  const getSelectionRowId = useCallback(
    (item, index) => {
      if (typeof selection?.getRowId === 'function') return selection.getRowId(item, index);
      return item?._id || `${index}`;
    },
    [selection]
  );

  const toggleSelectionRow = useCallback(
    (rowId, checked) => {
      if (!selectionEnabled || !setSelectionSelectedIds) return;
      setSelectionSelectedIds(prev => {
        const next = new Set(Array.isArray(prev) ? prev : []);
        if (checked) next.add(rowId);
        else next.delete(rowId);
        return Array.from(next);
      });
    },
    [selectionEnabled, setSelectionSelectedIds]
  );

  const toggleSelectionAllVisible = useCallback(
    (visibleItems, checked) => {
      if (!selectionEnabled || !setSelectionSelectedIds) return;
      const ids = (Array.isArray(visibleItems) ? visibleItems : []).map((it, idx) =>
        getSelectionRowId(it, idx)
      );
      if (ids.length === 0) return;
      setSelectionSelectedIds(prev => {
        const next = new Set(Array.isArray(prev) ? prev : []);
        if (checked) {
          ids.forEach(id => next.add(id));
        } else {
          ids.forEach(id => next.delete(id));
        }
        return Array.from(next);
      });
    },
    [selectionEnabled, setSelectionSelectedIds, getSelectionRowId]
  );

  // Mobile nested navigation
  const [mobileNestedContext, setMobileNestedContext] = useState(null); // { parentId, path }
  // Per-instance id used to coordinate the native back button across nested EditableList levels
  const instanceIdRef = useRef(`el-${Math.random().toString(36).slice(2)}`);
  // Root element ref – used to locate the scrollable ancestor for scroll preservation
  const rootRef = useRef(null);
  const savedScrollTopRef = useRef(0);
  const shouldRestoreScrollRef = useRef(false);

  // Mobile card expansion state: { [itemId]: boolean }
  const [expandedCards, setExpandedCards] = useState({});
  // Client-side pagination for the mobile card view (used when no server/external pagination applies)
  const [cardPage, setCardPage] = useState(1);
  const [cardPageSize, setCardPageSize] = useState(() => {
    const n = Number(options?.mobilePageSize);
    return Number.isFinite(n) && n > 0 ? n : 20;
  });

  // Responsive helpers
  const theme = useTheme();
  const isCardView = useMediaQuery(theme.breakpoints.down('md')); // render cards < md
  const isXsDown = useMediaQuery(theme.breakpoints.down('sm')); // very small screens
  const isLargeDesktop = useMediaQuery('(min-width:1500px)');
  const isMidDesktop = !isCardView && !isLargeDesktop; // between md and 1499px

  const parentFieldSchema = fieldSchema;
  const isSingleObjectListContext = nested && fieldSchema?.type === 'object';
  const creationDisabled = Boolean(options?.disableCreate);
  const deletionDisabled = Boolean(options?.disableDelete);

  // Helper: recursively search object tree for an object with matching _id
  const findObjectById = (obj, targetId) => {
    if (!obj || typeof obj !== 'object') return null;
    if (obj._id === targetId) return obj;
    for (const key of Object.keys(obj)) {
      const val = obj[key];
      if (typeof val === 'object') {
        const found = Array.isArray(val)
          ? val.map(v => findObjectById(v, targetId)).find(Boolean)
          : findObjectById(val, targetId);
        if (found) return found;
      }
    }
    return null;
  };

  // Memoize styles to prevent re-renders
  const searchFieldStyles = useMemo(
    () => ({
      width: isCardView ? '100%' : 'auto',
      minWidth: isCardView ? undefined : 220,
      '& .MuiOutlinedInput-root': {
        backgroundColor: 'transparent',
        color: 'text.primary',
        '&:hover': {
          backgroundColor: 'action.hover',
        },
        '&.Mui-focused': {
          backgroundColor: 'action.hover',
        },
      },
      '& .MuiInputBase-input': {
        height: 40,
        padding: '0 14px',
      },
    }),
    [isCardView]
  );

  const stickyHeaderCellSx = useMemo(
    () => ({
      backgroundColor: theme.palette.background.paper,
      zIndex: nested ? 2 : 20,
    }),
    [nested, theme]
  );

  const shouldFillTableHeight = !nested && options?.fillHeight !== false;
  const tablePaperSx = useMemo(() => {
    const base = {
      display: 'flex',
      flexDirection: 'column',
      minHeight: 0,
      position: 'relative',
    };
    if (!shouldFillTableHeight) return { ...base, maxHeight: '80vh' };
    return {
      ...base,
      height: options?.fillHeightValue || 'calc(100dvh - 140px)',
      maxHeight: 'none',
    };
  }, [shouldFillTableHeight, options?.fillHeightValue]);

  const isColumnSelectorEnabled = useMemo(
    () => !isSingleObjectListContext && !hideColumnSelector,
    [hideColumnSelector, isSingleObjectListContext]
  );

  const openColumnMenu = useCallback((e) => {
    e.stopPropagation();
    setColumnMenuAnchorEl(e.currentTarget);
  }, []);

  const closeColumnMenu = useCallback(() => setColumnMenuAnchorEl(null), []);

  const toggleVisibleColumn = useCallback((key) => {
    setVisibleColumns((prev) => {
      const current = Array.isArray(prev) ? prev : [];
      if (current.includes(key)) return current.filter((k) => k !== key);
      return [...current, key];
    });
  }, []);

  const ColumnSelectorTrigger = useMemo(() => {
    if (!isColumnSelectorEnabled) return null;
    return (
      <Tooltip title='Spalten auswählen' arrow>
        <IconButton size='small' onClick={openColumnMenu}>
          <ViewColumnIcon fontSize='small' />
        </IconButton>
      </Tooltip>
    );
  }, [isColumnSelectorEnabled, openColumnMenu]);

  // Restrict a select field's options to the ids that are assigned on the parent
  // item. Used e.g. so per-entertainment override dropdowns only offer the
  // packages/upsells that are actually assigned to the product (see
  // `filterOptionsByParentField` in the field schema).
  const filterOptionsByParent = useCallback(
    (fieldSchema, opts) => {
      const filterField = fieldSchema?.filterOptionsByParentField;
      if (!filterField || !parentDataItem || !Array.isArray(opts)) return opts;
      const assigned = parentDataItem[filterField];
      if (!Array.isArray(assigned)) return opts;
      const allowedIds = new Set(
        assigned
          .map(entry =>
            entry && typeof entry === 'object'
              ? (entry.upgradeId ?? entry._id ?? entry.value)
              : entry
          )
          .filter(id => id !== undefined && id !== null)
          .map(String)
      );
      return opts.filter(opt => allowedIds.has(String(opt.value)));
    },
    [parentDataItem]
  );

  // Memoize options for better performance
  const memoizedOptions = useMemo(() => {
    const options = {};
    Object.keys(schema).forEach(key => {
      const fieldSchema = schema[key];
      if (fieldSchema.api?.resource) {
        options[key] = apiOptions[key] || [];
      }
    });
    return options;
  }, [schema, apiOptions]);

  // apiOptions with per-field parent filtering applied, for dialogs/detail views.
  const filteredApiOptions = useMemo(() => {
    if (!parentDataItem) return apiOptions;
    const result = { ...apiOptions };
    Object.keys(schema).forEach(key => {
      const fieldSchema = schema[key];
      if (fieldSchema?.filterOptionsByParentField && Array.isArray(apiOptions[key])) {
        result[key] = filterOptionsByParent(fieldSchema, apiOptions[key]);
      }
    });
    return result;
  }, [apiOptions, schema, parentDataItem, filterOptionsByParent]);

  // apiOptions filtered for the "add first nested entry" dialog, which is rendered
  // at this (parent) level but adds rows into a nested schema. Options for fields
  // declaring `filterOptionsByParentField` are restricted to the ids assigned on
  // the parent row the dialog targets.
  const emptyNestedApiOptions = useMemo(() => {
    const nestedSchema = emptyNestedDialog.schema;
    if (!nestedSchema) return apiOptions;
    const parentRow = Array.isArray(items)
      ? items.find(it => it._id === emptyNestedDialog.parentId)
      : null;
    if (!parentRow) return apiOptions;
    const result = { ...apiOptions };
    Object.keys(nestedSchema).forEach(key => {
      const fieldSchema = nestedSchema[key];
      const filterField = fieldSchema?.filterOptionsByParentField;
      if (!filterField || !Array.isArray(apiOptions[key])) return;
      const assigned = parentRow[filterField];
      if (!Array.isArray(assigned)) return;
      const allowedIds = new Set(
        assigned
          .map(entry =>
            entry && typeof entry === 'object'
              ? (entry.upgradeId ?? entry._id ?? entry.value)
              : entry
          )
          .filter(id => id !== undefined && id !== null)
          .map(String)
      );
      result[key] = apiOptions[key].filter(opt => allowedIds.has(String(opt.value)));
    });
    return result;
  }, [apiOptions, items, emptyNestedDialog.schema, emptyNestedDialog.parentId]);

  const addLabel = useMemo(() => {
    if (typeof options?.addButtonLabel === 'string' && options.addButtonLabel.trim().length > 0) {
      return options.addButtonLabel.trim();
    }
    return isLargeDesktop ? title : 'Neu';
  }, [isLargeDesktop, title, options?.addButtonLabel]);

  const {
    isLoading,
    pagination,
    updateItem,
    deleteItem,
    addItem,
    duplicateItemPayload,
    fetchInitialData,
    fetchApiOptions,
  } = useEditableList(resourceType, schema, authCtx.token, options?.listConfig || null);

  const hasServerPagination = Boolean(externalPagination) || Boolean(pagination?.isFromServer);

  const serverSideSortingEnabled =
    serverSideSortingWanted && hasServerPagination;
  const serverSideFilteringEnabled =
    serverSideFilteringWanted && hasServerPagination;
  const serverSideListEnabled = serverSideSortingEnabled || serverSideFilteringEnabled;

  const transformItemsRef = useRef(options?.transformItems);
  useEffect(() => {
    transformItemsRef.current = options?.transformItems;
  }, [options?.transformItems]);

  // Prevent stale responses from overwriting newer optimistic UI state (e.g. rapid multi-select toggles).
  const fieldUpdateSeqRef = useRef(new Map()); // `${nestedEndpoint}|${targetId}|${fieldKey}` -> number

  // Effective server-side sort/filter request params, derived outside the fetch
  // effect. The fetch effect depends on these instead of the raw
  // serverSideSorting/FilteringEnabled flags: those flags flip from false to true
  // once the first paginated response arrives (pagination.isFromServer), which
  // used to re-run the effect and load every list a second time on mount.
  const serverSortParams =
    serverSideSortingEnabled && sortConfig?.key
      ? { sortKey: sortConfig.key, sortDirection: sortConfig.direction }
      : null;

  const serverFiltersParam = useMemo(() => {
    if (!serverSideFilteringEnabled) return null;
    const activeFilters = Object.entries(columnFilters || {}).reduce((acc, [key, f]) => {
      if (!f || typeof f !== 'object' || !f.op) return acc;
      // A column may filter on a different backend field than it displays — the
      // entertainment column shows the brand name but has to filter on the
      // venue id, since several venues share one brand name.
      const serverKey = schema[key]?.serverFilterKey || key;
      acc[serverKey] = { op: f.op, value: f.value };
      return acc;
    }, {});
    return Object.keys(activeFilters).length > 0 ? JSON.stringify(activeFilters) : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverSideFilteringEnabled, JSON.stringify(columnFilters)]);

  useEffect(() => {
    const fetchAll = async () => {
      // Always load option data so that select / multi-select fields render correctly
      const fetchedApiOptions = await fetchApiOptions(extraParams);
      setApiOptions(fetchedApiOptions);

      if (!nested) {
        const fetched = await fetchInitialData(currentPage, itemsPerPage, currentSearchTerm, {
          includeInactive: false,
          ...extraParams,
          ...(serverSortParams || {}),
          ...(serverFiltersParam ? { filters: serverFiltersParam } : {}),
        });
        const processedItems = transformItemsRef.current
          ? Array.isArray(fetched)
            ? fetched
                .flatMap(item => {
                  const out = transformItemsRef.current(item);
                  if (!out) return [];
                  return Array.isArray(out) ? out : [out];
                })
                .filter(Boolean)
            : fetched
          : fetched;
        setItems(processedItems);
      } else {
        // For nested lists we always use the data prop so that changes are immediately visible
        if (data && !Array.isArray(data)) {
          // Wrap single object into array for unified handling
          setItems([data]);
        } else {
          setItems(data || []);
        }
      }
    };

    fetchAll();
  }, [
    currentPage,
    itemsPerPage,
    currentSearchTerm,
    nested,
    data,
    JSON.stringify(extraParams),
    JSON.stringify(serverSortParams),
    serverFiltersParam,
    refreshTrigger,
  ]);

  // listen for external refresh events
  useEffect(() => {
    const handler = e => {
      if (!e?.detail?.resource) return;
      if (e.detail.resource !== resourceType) return;
      setCurrentPage(1);
      setRefreshTrigger(prev => prev + 1);
    };
    window.addEventListener('editableListRefresh', handler);
    return () => window.removeEventListener('editableListRefresh', handler);
  }, [resourceType]);

  // If parentIsEditable is provided, use that instead of checking permissions
  // Also check if the current field schema has readOnly set to true
  const isFieldReadOnly = nested && parentFieldSchema?.readOnly;
  const rightsResourceType = options?.rightsResourceType || resourceType;
  const topLevelCreateRight = `${rightsResourceType}:create`;
  const topLevelUpdateRight = `${rightsResourceType}:update`;
  const topLevelDeleteRight = `${rightsResourceType}:delete`;
  const nestedCreateRight = `${rightsResourceType}.${path}:create`;
  const nestedUpdateRight = `${rightsResourceType}.${path}:update`;
  const nestedDeleteRight = `${rightsResourceType}.${path}:delete`;

  const canCreate = isSingleObjectListContext
    ? false
    : isFieldReadOnly
      ? false
      : creationDisabled
        ? false
        : parentIsEditable ??
          authCtx?.checkRightAny(nested ? nestedCreateRight : topLevelCreateRight);
  const canUpdate = isFieldReadOnly
    ? false
    : parentIsEditable ??
      authCtx?.checkRightAny(nested ? nestedUpdateRight : topLevelUpdateRight);
  const canDelete = isSingleObjectListContext
    ? false
    : isFieldReadOnly
      ? false
      : deletionDisabled
        ? false
        : (parentIsEditable ??
            authCtx?.checkRightAny(nested ? nestedDeleteRight : topLevelDeleteRight)) ||
          authCtx?.checkRight(topLevelUpdateRight);
  const hasLeadingRowAction = typeof leadingRowAction === 'function';
  const detailViewEnabled = options?.disableDetailView !== true && !isSingleObjectListContext;
  const duplicationDisabled = Boolean(options?.disableDuplicate);
  const canDuplicate = Boolean(canCreate && !duplicationDisabled && !isSingleObjectListContext);
  const showActions =
    (canDelete || canDuplicate || typeof renderRowActions === 'function') && !isSingleObjectListContext;
  const showTrailingActions = showActions && !hideRowActionsColumn;

  const stickyLeftConfig = useMemo(() => {
    // Keep widths in sync between header/body to prevent visible gaps when columns are sticky.
    const selectionWidth = selectionEnabled ? 48 : 0;
    const createWidth = !isSingleObjectListContext && canCreate ? 52 : 0;
    const detailOpenWidth = detailViewEnabled ? 42 : 0;
    const leadingActionWidth = hasLeadingRowAction ? 48 : 0;

    const selectionLeft = 0;
    const createLeft = selectionLeft + selectionWidth;
    const detailOpenLeft = createLeft + createWidth;
    const leadingActionLeft = detailOpenLeft + detailOpenWidth;
    const firstDataLeft = leadingActionLeft + leadingActionWidth;

    return {
      selectionWidth,
      createWidth,
      detailOpenWidth,
      detailOpenLeft,
      leadingActionWidth,
      selectionLeft,
      createLeft,
      leadingActionLeft,
      firstDataLeft,
    };
  }, [selectionEnabled, isSingleObjectListContext, canCreate, detailViewEnabled, hasLeadingRowAction]);

  const stickyBodyCellSx = useMemo(
    () => ({
      position: 'sticky',
      zIndex: nested ? 2 : 18,
      backgroundImage: 'none',
    }),
    [nested]
  );

  const stickyFirstDataEdgeSx = useMemo(
    () => ({
      boxShadow: theme =>
        `4px 0 10px -2px ${alpha(theme.palette.common.black, theme.palette.mode === 'light' ? 0.12 : 0.35)}`,
    }),
    []
  );

  const stickyHeaderCellBaseSx = useMemo(
    () => ({
      ...stickyHeaderCellSx,
      position: 'sticky',
      zIndex: nested ? 3 : 25,
      borderRight: '1px solid',
      borderColor: 'divider',
    }),
    [stickyHeaderCellSx, nested]
  );

  // Opaque zebra + hover colors so horizontal scroll does not show through sticky columns
  // (MUI action.hover / action.selected are semi-transparent rgba).
  const getZebraRowBackground = useCallback(
    index =>
      index % 2 === 1
        ? theme.palette.mode === 'light'
          ? theme.palette.grey[100]
          : theme.palette.grey[800]
        : theme.palette.background.paper,
    [theme]
  );

  const rowHoverOpaqueBackground = useMemo(
    () =>
      theme.palette.mode === 'light' ? theme.palette.grey[200] : theme.palette.grey[700],
    [theme]
  );

  // Filter out nested items from schema for detail view
  const detailViewSchema = useMemo(() => {
    const filteredSchema = {};
    Object.entries(schema).forEach(([key, value]) => {
      if (value.type !== 'nested') {
        filteredSchema[key] = value;
      }
    });
    return filteredSchema;
  }, [schema]);

  const resolveNestedEndpoint = useCallback(() => {
    return fieldSchema.api?.nestedEndpoint
      ? nestedBasePath || `/api/v1/${resourceType}/${itemId}/${fieldSchema.api.nestedEndpoint}`
      : null;
  }, [fieldSchema, nestedBasePath, resourceType, itemId]);

  const applyAddResponse = useCallback(
    ({ submittedItem, response, nestedEndpoint }) => {
      if (!response) {
        return;
      }

      if (!nestedEndpoint) {
        const maybeTransform = transformItemsRef.current;
        const transformed = maybeTransform ? maybeTransform(response) : response;
        const toAppend = transformed
          ? Array.isArray(transformed)
            ? transformed
            : [transformed]
          : [response];

        setItems(prevItems => {
          const prev = Array.isArray(prevItems) ? prevItems : [];
          return [...prev, ...toAppend];
        });
        return;
      }

      onItemAdd && onItemAdd(response);
      if (response[path] !== undefined) {
        setItems(Array.isArray(response[path]) ? response[path] : [response[path]]);
      } else {
        setItems(prev => [...(Array.isArray(prev) ? prev : []), submittedItem]);
      }
    },
    [onItemAdd, path]
  );

  const handleAddItem = async newItem => {
    const nestedEndpoint = resolveNestedEndpoint();
    if (nested && !nestedEndpoint) {
      toast.error('Kein Endpunkt für diese verschachtelte Liste konfiguriert');
      return;
    }

    const response = await addItem(newItem, nestedEndpoint);
    applyAddResponse({ submittedItem: newItem, response, nestedEndpoint });
  };

  const handleDuplicateItem = useCallback(
    async sourceItem => {
      try {
        if (!sourceItem) return;

        const duplicated = await duplicateItemPayload(sourceItem);

        const nestedEndpoint = resolveNestedEndpoint();
        if (nested && !nestedEndpoint) {
          toast.error('Kein Endpunkt für diese verschachtelte Liste konfiguriert');
          return;
        }

        const response = await addItem(duplicated, nestedEndpoint, 'duplicate');
        applyAddResponse({ submittedItem: duplicated, response, nestedEndpoint });
      } catch (err) {
        toast.error('Fehler beim Duplizieren des Eintrags');
      }
    },
    [
      nested,
      addItem,
      duplicateItemPayload,
      resolveNestedEndpoint,
      applyAddResponse,
    ]
  );

  const handleDetailViewClose = useCallback(() => {
    setIsDetailViewOpen(false);
    setSelectedItem(null);
  }, []);

  const handleOpenDetailView = useCallback(item => {
    setSelectedItem(item);
    setIsDetailViewOpen(true);
  }, []);

  const handleItemSelect = useCallback(
    item => {
      // On very small / card view screens we inline-display everything unless inline editing is disabled.
      if (isCardView && !inlineEditingDisabled) return;
      if (options?.disableDetailView === true) return;
      setSelectedItem(item);
      setIsDetailViewOpen(true);
    },
    [isCardView, inlineEditingDisabled, options?.disableDetailView]
  );

  const handleOpenDialog = useCallback(() => {
    if (typeof options?.onCreateClick === 'function') {
      options.onCreateClick();
      return;
    }
    setIsDialogOpen(true);
  }, [options?.onCreateClick]);

  const handleCloseDialog = useCallback(() => {
    setIsDialogOpen(false);
  }, []);

  const handleDeleteClick = useCallback((itemId, itemName) => {
    setDeleteConfirmation({
      open: true,
      itemId,
      itemName,
    });
  }, []);

  const handleDeleteConfirm = async () => {
    if (deleteConfirmation.itemId) {
      const isSingleObject = Boolean(fieldSchema?.singleObject);
      const baseEndpoint = fieldSchema.api?.nestedEndpoint
        ? nestedBasePath
          ? nestedBasePath
          : `/api/v1/${resourceType}/${itemId}/${fieldSchema.api.nestedEndpoint}`
        : null;
      const nestedEndpoint = baseEndpoint
        ? isSingleObject
          ? baseEndpoint // single object: delete without nested id in path
          : `${baseEndpoint}/${deleteConfirmation.itemId}`
        : null;
      if (nested && !nestedEndpoint) {
        toast.error('Kein Endpunkt für diese verschachtelte Liste konfiguriert');
        return;
      }
      const success = await deleteItem(
        deleteConfirmation.itemId,
        nestedEndpoint,
        handleDeleteCancel
      );
      if (success) {
        if (!nestedEndpoint) {
          setItems(items.filter(item => item._id !== deleteConfirmation.itemId));
        } else {
          onItemDelete(deleteConfirmation.itemId);
        }
        setDeleteConfirmation({
          open: false,
          itemId: null,
          itemName: null,
        });
      } else {
        toast.error('Fehler beim Löschen des Eintrags');
      }
    }
  };

  const handleDeleteCancel = useCallback(() => {
    setDeleteConfirmation({
      open: false,
      itemId: null,
      itemName: null,
    });
  }, []);

  const handlePasswordDialogClose = useCallback(() => {
    setPasswordDialog({
      open: false,
      itemId: null,
      itemName: null,
    });
  }, []);

  const handlePasswordSave = useCallback(
    password => {
      updateItem(passwordDialog.itemId, { password });
    },
    [passwordDialog.itemId, updateItem]
  );

  const handleEmptyNestedDialogClose = useCallback(() => {
    setEmptyNestedDialog({
      open: false,
      parentId: null,
      path: null,
      schema: null,
    });
  }, []);

  const handleEmptyNestedAdd = useCallback(
    async newItem => {
      try {
        const parentFieldSchema = schema[emptyNestedDialog.path];
        const nestedEndpoint = parentFieldSchema?.api?.nestedEndpoint
          ? `${
              nestedBasePath
                ? `${nestedBasePath}/${emptyNestedDialog.parentId}`
                : `/api/v1/${resourceType}/${emptyNestedDialog.parentId}`
            }/${parentFieldSchema.api.nestedEndpoint}`
          : null;

        if (!nestedEndpoint) {
          toast.error('Kein Endpunkt für diese verschachtelte Liste konfiguriert');
          return;
        }

        const response = await addItem(newItem, nestedEndpoint);
        if (response) {
          setItems(prevItems => {
            return prevItems.map(item => {
              if (item._id !== emptyNestedDialog.parentId) return item;

              // Versuche den Container (Parent) im Server-Response zu finden
              const container = findObjectById(response, emptyNestedDialog.parentId) || item;
              const listDataRaw =
                container[emptyNestedDialog.path] !== undefined
                  ? container[emptyNestedDialog.path]
                  : response;

              const listData = Array.isArray(listDataRaw) ? listDataRaw : [listDataRaw];

              return {
                ...item,
                [emptyNestedDialog.path]: listData,
              };
            });
          });
          handleEmptyNestedDialogClose();
        }
      } catch (error) {
        if (error.response?.data?.status === 'error' && error.response?.data?.data) {
          const validationErrors = error.response.data.data;
          console.error('Validation errors:', validationErrors);
          return;
        }
        toast.error('Fehler beim Hinzufügen des Eintrags');
        console.error('Error adding item:', error);
      }
    },
    [
      schema,
      resourceType,
      addItem,
      emptyNestedDialog,
      setItems,
      handleEmptyNestedDialogClose,
      nestedBasePath,
      findObjectById,
    ]
  );

  // Add helper callbacks for updating root items when nested list modifies
  const addNestedItemToParent = useCallback((parentId, path, response) => {
    setItems(prevItems => {
      if (response && response._id) {
        return prevItems.map(it => (it._id === response._id ? response : it));
      }
      return prevItems.map(item => {
        if (item._id === parentId) {
          if (response && response[path]) {
            return {
              ...item,
              [path]: response[path],
            };
          }
          return {
            ...item,
            [path]: Array.isArray(item[path]) ? [...item[path], response] : [response],
          };
        }
        return item;
      });
    });
  }, []);

  const updateNestedItemInParent = useCallback((parentId, path, response) => {
    setItems(prevItems => {
      if (response && response._id) {
        return prevItems.map(it => (it._id === response._id ? response : it));
      }
      return prevItems.map(item => (item._id === parentId ? response : item));
    });
  }, []);

  const deleteNestedItemFromParent = useCallback((parentId, path, deletedNestedId) => {
    setItems(prevItems =>
      prevItems.map(item => {
        if (item._id === parentId) {
          return {
            ...item,
            [path]: Array.isArray(item[path])
            ? item[path].filter(n => n._id !== deletedNestedId)
            : item[path],
          };
        }
        return item;
      })
    );
  }, []);

  // A view can hand in a new search term after mount — the header search sends
  // the user to the Buchungssuche with a query even while that view is open.
  useEffect(() => {
    const nextTerm = initialSearchTerm || '';
    if (!nextTerm) return;
    setActiveSearchTerm(nextTerm);
    setCurrentSearchTerm(nextTerm);
    setCurrentPage(1);
  }, [initialSearchTerm]);

  const handleSearchKeyDown = useCallback(e => {
    if (e.key === 'Enter') {
      const searchValue = e.target.value || '';
      setActiveSearchTerm(searchValue);
      setCurrentSearchTerm(searchValue);
      setCurrentPage(1);
      if (searchInputRef.current) {
        searchInputRef.current.value = '';
      }
    }
  }, []);

  const handlePageChange = useCallback(newPage => {
    setCurrentPage(newPage);
  }, []);

  const handleItemsPerPageChange = useCallback(event => {
    const newValue = parseInt(event.target.value);
    setItemsPerPage(newValue);
    setCurrentPage(1); // Reset to first page when changing items per page
  }, []);

  const toggleExpand = useCallback((itemId, pathKey) => {
    const key = `${itemId}:${pathKey}`;
    setExpandedItems(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
  }, []);

  const toggleCardExpand = useCallback(rowId => {
    if (rowId === null || rowId === undefined) return;
    setExpandedCards(prev => ({ ...prev, [rowId]: !prev[rowId] }));
  }, []);

  // Find the nearest scrollable ancestor so we can preserve/restore the scroll
  // position when navigating in and out of nested lists on mobile.
  const getScrollContainer = useCallback(() => {
    let el = rootRef.current?.parentElement;
    while (el) {
      const style = window.getComputedStyle(el);
      const overflowY = style.overflowY;
      if ((overflowY === 'auto' || overflowY === 'scroll') && el.scrollHeight > el.clientHeight) {
        return el;
      }
      el = el.parentElement;
    }
    return null;
  }, []);

  // Enter a nested list on mobile. Pushes a history entry so the native/browser
  // back button navigates one level up instead of leaving the page, and stores
  // the current scroll position so it can be restored on the way back.
  const enterMobileNested = useCallback(
    ctx => {
      const container = getScrollContainer();
      savedScrollTopRef.current = container ? container.scrollTop : window.scrollY || 0;
      try {
        mobileNestedBackStack.push(instanceIdRef.current);
        // Preserve any existing history state (e.g. React Router bookkeeping) so
        // that consuming this entry via back() does not trigger a route change.
        window.history.pushState(
          { ...(window.history.state || {}), fzEditableListNested: true },
          ''
        );
      } catch {
        /* history API unavailable – navigation still works without back integration */
      }
      setMobileNestedContext(ctx);
    },
    [getScrollContainer]
  );

  // Leave the nested list. Routes through history.back() so the pushed entry is
  // consumed and the popstate handler keeps everything in sync.
  const exitMobileNested = useCallback(() => {
    if (mobileNestedBackStack[mobileNestedBackStack.length - 1] === instanceIdRef.current) {
      try {
        window.history.back();
        return;
      } catch {
        /* fall through to manual close */
      }
    }
    mobileNestedBackStack = mobileNestedBackStack.filter(
      token => token !== instanceIdRef.current
    );
    shouldRestoreScrollRef.current = true;
    setMobileNestedContext(null);
  }, []);

  // Handle the native back button: only the deepest open nested level reacts.
  useEffect(() => {
    if (!isCardView) return undefined;
    const handlePopState = () => {
      const top = mobileNestedBackStack[mobileNestedBackStack.length - 1];
      if (top !== instanceIdRef.current) return;
      mobileNestedBackStack.pop();
      shouldRestoreScrollRef.current = true;
      setMobileNestedContext(null);
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [isCardView]);

  // Drop any leftover back-stack token belonging to this instance on unmount.
  useEffect(() => {
    const id = instanceIdRef.current;
    return () => {
      mobileNestedBackStack = mobileNestedBackStack.filter(token => token !== id);
    };
  }, []);

  // Restore the saved scroll position once we have returned to the parent list.
  useEffect(() => {
    if (mobileNestedContext) return;
    if (!shouldRestoreScrollRef.current) return;
    shouldRestoreScrollRef.current = false;
    const top = savedScrollTopRef.current || 0;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const container = getScrollContainer();
        if (container) container.scrollTop = top;
        else window.scrollTo(0, top);
      });
    });
  }, [mobileNestedContext, getScrollContainer]);

  const handleFieldUpdate = useCallback(
    async (key, newValue, item) => {
      let processedValue = newValue;
      let updatesToSend = { [key]: newValue };

      if (options?.transformFieldUpdate) {
        processedValue = options.transformFieldUpdate(key, newValue, item) ?? newValue;
        updatesToSend[key] = processedValue;
      }

      if (options?.transformBeforeSave) {
        const transformed = options.transformBeforeSave(key, processedValue, item);
        if (transformed && typeof transformed === 'object') {
          updatesToSend = { ...updatesToSend, ...transformed };
        }
      }

      const subSchema = schema[key];

      // Validate field before updating (normalize price to number for validator)
      if (subSchema) {
        const { validateField } = await import('../../util/validation');
        const schemaForValidation =
          subSchema.type === 'price' ? { ...subSchema, type: 'number' } : subSchema;
        const validation = validateField(newValue, schemaForValidation);
        if (!validation.isValid) {
          toast.error('Fehler beim Validieren des Eintrags');
          return validation.errors[0];
        }
      }

      const nestedEndpoint = (() => {
        if (!(fieldSchema.api?.nestedEndpoint && item?._id)) return null;
        if (nestedBasePath) {
          if (fieldSchema?.singleObject) {
            return nestedBasePath;
          }
          // If nestedBasePath contains a placeholder, replace it
          if (nestedBasePath.includes(':entryId')) {
            return nestedBasePath.replace(':entryId', item._id);
          }
          // Allow extra path segment after nestedBasePath (e.g., orderedResources/:id/resource)
          return `${nestedBasePath}/${item._id}`;
        }
        // Support nested paths that may include an intermediate subdocument id that we don't have
        // We normalize to both patterns on backend, so stick to base form without extra id
        return `/api/v1/${resourceType}/${itemId}/${fieldSchema.api.nestedEndpoint}/${item._id}`;
      })();

      const targetId = item?._id || itemId; // fall back to parent id for object fields without _id
      const updateTokenKey = `${nestedEndpoint || ''}|${targetId}|${key}`;
      const nextToken = (fieldUpdateSeqRef.current.get(updateTokenKey) || 0) + 1;
      fieldUpdateSeqRef.current.set(updateTokenKey, nextToken);
      const isLatest = () => fieldUpdateSeqRef.current.get(updateTokenKey) === nextToken;

      // Optimistic UI update: apply local changes immediately and rollback on error.
      const previousSnapshot = (() => {
        if (!Array.isArray(items)) return null;
        const current = items.find(it => it?._id === targetId);
        if (!current) return null;
        const snap = {};
        Object.keys(updatesToSend).forEach(updateKey => {
          snap[updateKey] = current?.[updateKey];
        });
        return snap;
      })();

      const applyOptimisticUpdate = () => {
        if (!Array.isArray(items)) return;
        setItems(prevItems => {
          if (!Array.isArray(prevItems)) return prevItems;
          return prevItems.map(it => {
            if (it?._id !== targetId) return it;
            const updatedItem = { ...it };
            Object.keys(updatesToSend).forEach(updateKey => {
              updatedItem[updateKey] = updatesToSend[updateKey];
            });
            const transformedItem = options?.transformItems ? options.transformItems(updatedItem) : updatedItem;
            if (selectedItem?._id === targetId) {
              setSelectedItem(transformedItem);
            }
            return transformedItem;
          });
        });
      };

      const rollbackOptimisticUpdate = () => {
        if (!isLatest()) return;
        if (!previousSnapshot || !Array.isArray(items)) return;
        setItems(prevItems => {
          if (!Array.isArray(prevItems)) return prevItems;
          return prevItems.map(it => {
            if (it?._id !== targetId) return it;
            const reverted = { ...it };
            Object.keys(previousSnapshot).forEach(updateKey => {
              reverted[updateKey] = previousSnapshot[updateKey];
            });
            const transformed = options?.transformItems ? options.transformItems(reverted) : reverted;
            if (selectedItem?._id === targetId) {
              setSelectedItem(transformed);
            }
            return transformed;
          });
        });
      };

      if (nestedEndpoint) {
        applyOptimisticUpdate();
        try {
          const data = await updateItem(targetId, updatesToSend, nestedEndpoint);
          if (!isLatest()) return true;
          if (!data) {
            rollbackOptimisticUpdate();
            toast.error('Fehler beim Speichern des Eintrags');
            return 'Fehler beim Speichern';
          }
          onItemUpdate(data, item._id);
          return true;
        } catch (error) {
          if (!isLatest()) return true;
          rollbackOptimisticUpdate();
          toast.error('Fehler beim Speichern des Eintrags');
          return error.response?.data?.message || 'Fehler beim Speichern';
        }
      }

      if (nested && parentFieldSchema?.type === 'object') {
        const parentKey = path;
        const updatedObject = { ...item, ...updatesToSend };
        // Optimistically update the parent object in-place
        const prev = item?.[parentKey];
        setItems(prevItems =>
          Array.isArray(prevItems)
            ? prevItems.map(it => (it?._id === itemId ? { ...it, [parentKey]: updatedObject } : it))
            : prevItems
        );
        try {
          const data = await updateItem(itemId, { [parentKey]: updatedObject }, null);
          if (!isLatest()) return true;
          if (!data) {
            // rollback
            setItems(prevItems =>
              Array.isArray(prevItems)
                ? prevItems.map(it => (it?._id === itemId ? { ...it, [parentKey]: prev } : it))
                : prevItems
            );
            toast.error('Fehler beim Speichern des Eintrags');
            return 'Fehler beim Speichern';
          }
          onItemUpdate && onItemUpdate(data, itemId);
          return true;
        } catch (error) {
          if (!isLatest()) return true;
          setItems(prevItems =>
            Array.isArray(prevItems)
              ? prevItems.map(it => (it?._id === itemId ? { ...it, [parentKey]: prev } : it))
              : prevItems
          );
          toast.error('Fehler beim Speichern des Eintrags');
          return error.response?.data?.message || 'Fehler beim Speichern';
        }
      }

      try {
        applyOptimisticUpdate();
        const data = await updateItem(targetId, updatesToSend, nestedEndpoint);
        if (!isLatest()) return true;
        if (!data) {
          rollbackOptimisticUpdate();
          toast.error('Fehler beim Speichern des Eintrags');
          return 'Fehler beim Speichern';
        }
        setItems(prevItems => {
          if (!Array.isArray(prevItems)) return prevItems;
          return prevItems.map(it => {
            if (it?._id === targetId) {
              // If backend returned full document, prefer it
              if (data && data._id === targetId && typeof data === 'object') {
                // Transform the data if transformItems is provided
                const transformedData = options?.transformItems
                  ? options.transformItems(data)
                  : data;
                // Also update selectedItem if it's the same item
                if (selectedItem?._id === targetId) {
                  setSelectedItem(transformedData);
                }
                return transformedData;
              }
              // Apply all updates from updatesToSend to the item
              const updatedItem = { ...it };
              Object.keys(updatesToSend).forEach(updateKey => {
                if (data && data[updateKey] !== undefined) {
                  updatedItem[updateKey] = data[updateKey];
                } else {
                  updatedItem[updateKey] = updatesToSend[updateKey];
                }
              });
              // Transform the updated item if transformItems is provided
              const transformedItem = options?.transformItems
                ? options.transformItems(updatedItem)
                : updatedItem;
              // Also update selectedItem if it's the same item
              if (selectedItem?._id === targetId) {
                setSelectedItem(transformedItem);
              }
              return transformedItem;
            }
            return it;
          });
        });
        return true;
      } catch (error) {
        if (!isLatest()) return true;
        rollbackOptimisticUpdate();
        toast.error('Fehler beim Speichern des Eintrags');
        return error.response?.data?.message || 'Fehler beim Speichern';
      }
    },
    [
      schema,
      updateItem,
      fieldSchema,
      resourceType,
      itemId,
      onItemUpdate,
      authCtx.token,
      nested,
      path,
      options,
      selectedItem,
      setSelectedItem,
      items,
    ]
  );

  const renderNestedContent = useCallback(
    (value, path, parentId) => {
      if (!value || typeof value !== 'object') {
        const field = schema[path];
        const currentSchema = field?.schema;
        if (!currentSchema) return null;
        // Only show add button for true nested lists that have an API endpoint
        const isAddAllowed =
          field?.type === 'nested' && !!field?.api?.nestedEndpoint && !field?.readOnly && canCreate;
        if (!isAddAllowed) {
          return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <Typography variant='body2' color='text.secondary'>
                Keine Einträge vorhanden
              </Typography>
            </Box>
          );
        }
        return (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Button
              variant='outlined'
              size='small'
              startIcon={<AddIcon />}
              onClick={e => {
                e.stopPropagation();
                setEmptyNestedDialog({
                  open: true,
                  parentId,
                  path,
                  schema: currentSchema,
                });
              }}
              sx={{
                borderColor: 'grey.400',
                color: 'text.primary',
                textTransform: 'none',
                '&:hover': {
                  borderColor: 'grey.600',
                  backgroundColor: 'action.hover',
                },
                minWidth: 120,
                height: 32,
                px: 1,
              }}
            >
              <LockIconWrapper right={`${rightsResourceType}:create`}>Hinzufügen</LockIconWrapper>
            </Button>
          </Box>
        );
      }

      const currentSchema = schema[path]?.schema;
      if (!currentSchema) {
        return null;
      }

      const itemsToShow = Array.isArray(value) ? value : [value];
      const itemCount = itemsToShow.length;

      if (itemCount === 0) {
        // Don't show add button if the nested field is readOnly
        if (schema[path]?.readOnly) {
          return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <Typography variant='body2' color='text.secondary'>
                Keine Einträge vorhanden
              </Typography>
            </Box>
          );
        }

        return (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Button
              variant='outlined'
              size='small'
              startIcon={<AddIcon />}
              onClick={e => {
                e.stopPropagation();
                setEmptyNestedDialog({
                  open: true,
                  parentId,
                  path,
                  schema: currentSchema,
                });
              }}
              sx={{
                borderColor: 'grey.400',
                color: 'text.primary',
                textTransform: 'none',
                '&:hover': {
                  borderColor: 'grey.600',
                  backgroundColor: 'action.hover',
                },
                minWidth: 120,
                height: 32,
                px: 1,
              }}
            >
              <LockIconWrapper right={`${rightsResourceType}:create`}>Hinzufügen</LockIconWrapper>
            </Button>
          </Box>
        );
      }

      const isExpanded = expandedItems[`${parentId}:${path}`];
      // const displayedItems = itemsToShow.map(item => {
      //   const displayedItem = { ...item };
      //   Object.keys(currentSchema).forEach(key => {
      //     if (currentSchema[key].displayValue && item[key] !== undefined) {
      //       displayedItem[key] = currentSchema[key].displayValue(item[key]);
      //     }
      //   });
      //   return displayedItem;
      // });

      // Card view: show navigation button instead of inline list
      if (isCardView) {
        return (
          <Button
            variant='text'
            size='small'
            onClick={e => {
              e.stopPropagation();
              enterMobileNested({ parentId, path });
            }}
            sx={{ textTransform: 'none', minWidth: 0, px: 1 }}
          >
            {schema[path]?.type === 'object'
              ? 'Ansehen'
              : `${itemCount} ${itemCount === 1 ? 'Eintrag' : 'Einträge'} ansehen`}
          </Button>
        );
      }

      return (
        <Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              '&:hover': { backgroundColor: 'action.hover' },
            }}
            onClick={e => {
              e.stopPropagation();
              toggleExpand(parentId, path);
            }}
          >
            <IconButton
              size='small'
              onClick={e => {
                e.stopPropagation();
                toggleExpand(parentId, path);
              }}
            >
              {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
            <Typography variant='body2'>
              {schema[path]?.type === 'object'
                ? 'Ansehen'
                : `${itemCount} ${itemCount === 1 ? 'Eintrag' : 'Einträge'}`}
            </Typography>
          </Box>
          <Collapse in={isExpanded}>
            {isExpanded && (
              <Box onClick={e => e.stopPropagation()}>
                <EditableList
                  key={`${parentId}-${path}`}
                  itemId={parentId}
                  onItemAdd={response => {
                    setItems(prevItems => {
                      if (response && response._id) {
                        const hasMatch = prevItems.some(it => it._id === response._id);
                        if (hasMatch) {
                          return prevItems.map(it => (it._id === response._id ? response : it));
                        }
                      }

                      // Generic: server might have returned a higher-level container (e.g. product)
                      const container = findObjectById(response, parentId);
                      if (container && container[path] !== undefined) {
                        return prevItems.map(it =>
                          it._id === parentId ? { ...it, [path]: container[path] } : it
                        );
                      }

                      return prevItems.map(it =>
                        it._id === parentId
                          ? {
                              ...it,
                              [path]: Array.isArray(it[path])
                                ? [...it[path], response]
                                : [response],
                            }
                          : it
                      );
                    });
                  }}
                  onItemUpdate={response => {
                    setItems(prevItems => {
                      if (response && response._id) {
                        const hasMatch = prevItems.some(it => it._id === response._id);
                        if (hasMatch) {
                          return prevItems.map(it => (it._id === response._id ? response : it));
                        }
                      }
                      const container = findObjectById(response, parentId);
                      if (container && container[path] !== undefined) {
                        return prevItems.map(it =>
                          it._id === parentId ? { ...it, [path]: container[path] } : it
                        );
                      }
                      return prevItems;
                    });
                  }}
                  onItemDelete={deletedNestedId => {
                    setItems(prevItems =>
                      prevItems.map(item => {
                        if (item._id !== parentId) return item;
                        const current = item[path];
                        return {
                          ...item,
                          [path]: Array.isArray(current)
                            ? current.filter(nestedIt => nestedIt._id !== deletedNestedId)
                            : undefined,
                        };
                      })
                    );
                  }}
                  fieldSchema={schema[path]}
                  schema={currentSchema}
                  title={(() => {
                    const fieldSchema = schema[path];
                    const fullTitle = fieldSchema?.addButtonText || `${fieldSchema?.label || path}`;
                    return isXsDown ? fullTitle : 'Neu';
                  })()}
                  nested={true}
                  resourceType={resourceType}
                  path={path}
                  parentIsEditable={canUpdate}
                  data={items.find(item => item._id === parentId)?.[path]}
                  parentDataItem={items.find(item => item._id === parentId)}
                  nestedBasePath={(() => {
                    const field = schema[path];
                    if (!field?.api?.nestedEndpoint) return null;
                    // Support dynamic placeholder :entryId for deeper nesting
                    const endpoint = field.api.nestedEndpoint;
                    const parentIsSingleObject = Boolean(field?.singleObject);
                    const base = nestedBasePath
                      ? parentIsSingleObject
                        ? `${nestedBasePath}`
                        : `${nestedBasePath}/${parentId}`
                      : `/api/v1/${resourceType}/${parentId}`;
                    return `${base}/${endpoint}`;
                  })()}
                />
              </Box>
            )}
          </Collapse>
        </Box>
      );
    },
    [
      schema,
      expandedItems,
      toggleExpand,
      resourceType,
      canUpdate,
      items,
      setItems,
      onItemAdd,
      onItemUpdate,
      onItemDelete,
      isCardView,
      enterMobileNested,
    ]
  );

  const renderObjectContent = useCallback(
    (value, path) => {
      const field = schema[path];
      const displayed = field?.displayValue
        ? field.displayValue(value)
        : value && typeof value === 'object'
          ? Object.values(value).join(' | ')
          : '-';
      return (
        <Typography variant='body2' sx={{ whiteSpace: 'pre-wrap' }}>
          {displayed || '—'}
        </Typography>
      );
    },
    [schema]
  );

  const renderField = useCallback(
    (key, value, item) => {
      const fieldSchema = schema[key];
      if (!fieldSchema) return null;

      const getOptions = () => {
        if (fieldSchema.api?.resource) {
          return memoizedOptions[key] || [];
        }
        if (fieldSchema.getOptions) {
          return fieldSchema.getOptions(item) || [];
        }
        return fieldSchema.options || [];
      };
      const options = filterOptionsByParent(fieldSchema, getOptions());

      const isEditableField = !fieldSchema.readOnly && canUpdate && !inlineEditingDisabled;

      if (fieldSchema.type === 'password') {
        return (
          <EditablePassword
            value={value}
            setValue={newValue => handleFieldUpdate(key, newValue, item)}
            isEditable={isEditableField}
            label={isCardView ? null : fieldSchema.label}
            required={fieldSchema.required}
            showValidation={true}
            schema={fieldSchema}
          />
        );
      }
      if (fieldSchema.type === 'select' || fieldSchema.type === 'multi-select') {
        // Normalize the incoming value – for single-select we expect a primitive,
        // for multi-select an array.
        let normalizedValue;

        if (fieldSchema.type === 'multi-select') {
          normalizedValue = Array.isArray(value) ? value : value ? [value] : [];
        } else {
          // single select
          // If an array sneaks in, take first element, else keep primitive/null.
          normalizedValue = Array.isArray(value) ? (value[0] ?? '') : (value ?? '');
        }

        // Map through api.idName or api.itemField → actual primitive values for the Select element
        const mappedValue = (() => {
          const idKey = fieldSchema.api?.idName;
          const itemKey = fieldSchema.api?.itemField;
          const valueKey = idKey || itemKey;
          if (!valueKey) return normalizedValue;
          if (Array.isArray(normalizedValue)) {
            return normalizedValue.map(v => (v && typeof v === 'object' ? v[valueKey] : v));
          }
          if (normalizedValue && typeof normalizedValue === 'object') {
            return normalizedValue[valueKey];
          }
          return normalizedValue;
        })();

        return (
          <EditableSelect
            value={mappedValue}
            setValue={newValue => handleFieldUpdate(key, newValue, item)}
            isEditable={isEditableField}
            type={fieldSchema.type}
            options={options}
            getOptions={fieldSchema.getOptions}
            parentItem={item}
            label={isCardView ? null : fieldSchema.label}
            placeholder={!fieldSchema.readOnly ? fieldSchema.placeholder : '-'}
            required={fieldSchema.required}
            showValidation={true}
            schema={fieldSchema}
          />
        );
      }
      if (fieldSchema.type === 'boolean') {
        return (
          <EditableBoolean
            value={value}
            setValue={newValue => handleFieldUpdate(key, newValue, item)}
            isEditable={isEditableField}
            label={isCardView ? null : fieldSchema.label}
            required={fieldSchema.required}
            readOnly={fieldSchema.readOnly}
          />
        );
      }

      // PRICE (currency)
      if (fieldSchema.type === 'price') {
        return (
          <EditablePrice
            value={value}
            setValue={newValue => handleFieldUpdate(key, newValue, item)}
            isEditable={isEditableField}
            label={isCardView ? null : fieldSchema.label}
            placeholder={fieldSchema.placeholder || '0,00'}
            required={fieldSchema.required}
            showValidation={true}
            schema={fieldSchema}
          />
        );
      }

      if (fieldSchema.type === 'milliseconds') {
        const minutes =
          typeof value === 'number'
            ? value / 60000
            : value === null || value === undefined || value === ''
              ? null
              : Number(value) / 60000;

        const minutesValue = Number.isFinite(minutes) ? minutes : null;
        const schemaForInput = { ...fieldSchema, type: 'number' };

        return (
          <EditableText
            value={minutesValue}
            setValue={newValue => {
              if (newValue === '' || newValue === null || newValue === undefined) {
                return handleFieldUpdate(key, null, item);
              }
              const parsed = Number.parseFloat(String(newValue));
              if (!Number.isFinite(parsed)) return;
              return handleFieldUpdate(key, Math.round(parsed * 60000), item);
            }}
            isEditable={isEditableField}
            type={'number'}
            displayValue={undefined}
            label={isCardView ? null : fieldSchema.label}
            placeholder={fieldSchema.placeholder}
            schema={schemaForInput}
            required={fieldSchema.required}
            showValidation={true}
            clampLines={fieldSchema.previewInList ? fieldSchema.previewMaxLines || 2 : 0}
            hyphenate={Boolean(fieldSchema.useHyphens)}
          />
        );
      }

      // OBJECT (nested, single)
      if (fieldSchema.type === 'object') {
        const displayed = fieldSchema.displayValue
          ? fieldSchema.displayValue(value, item, memoizedOptions, fieldSchema)
          : value && typeof value === 'object'
            ? Object.values(value).join(' | ')
            : '-';

        return (
          <Typography variant='body2' sx={{ whiteSpace: 'pre-wrap' }}>
            {displayed || '—'}
          </Typography>
        );
      }
      // If a text field holds an Array/Object but provides a display formatter, coerce to string for editing
      if (
        fieldSchema.type === 'text' &&
        Array.isArray(value) &&
        (fieldSchema.listDisplayValue || fieldSchema.displayValue)
      ) {
        const toStringFn = fieldSchema.listDisplayValue || fieldSchema.displayValue;
        const stringValue = toStringFn
          ? toStringFn(value, item, memoizedOptions, fieldSchema)
          : String(value ?? '');
        return (
          <EditableText
            value={stringValue}
            setValue={newValue => handleFieldUpdate(key, newValue, item)}
            isEditable={isEditableField}
            type={fieldSchema.type}
            displayValue={undefined}
            label={isCardView ? null : fieldSchema.label}
            placeholder={fieldSchema.placeholder}
            schema={fieldSchema}
            required={fieldSchema.required}
            showValidation={true}
            clampLines={fieldSchema.previewInList ? fieldSchema.previewMaxLines || 2 : 0}
            hyphenate={Boolean(fieldSchema.useHyphens)}
          />
        );
      }

      // Handle array type fields (like skillGroups) that should use listDisplayValue
      if (fieldSchema.type === 'array' && fieldSchema.listDisplayValue) {
        return (
          <EditableText
            value={value}
            setValue={newValue => handleFieldUpdate(key, newValue, item)}
            isEditable={isEditableField}
            type={fieldSchema.type}
            displayValue={displayValueValue =>
              fieldSchema.listDisplayValue(displayValueValue, item, memoizedOptions, fieldSchema)
            }
            label={isCardView ? null : fieldSchema.label}
            placeholder={fieldSchema.placeholder}
            schema={fieldSchema}
            required={fieldSchema.required}
            showValidation={true}
            clampLines={fieldSchema.previewInList ? fieldSchema.previewMaxLines || 2 : 0}
            hyphenate={Boolean(fieldSchema.useHyphens)}
            truncate={Number.isFinite(fieldSchema.truncate) ? fieldSchema.truncate : 0}
            truncateMiddle={Boolean(fieldSchema.truncateMiddle)}
          />
        );
      }

      // Handle custom field types that have listDisplayValue - render as text
      if (fieldSchema.listDisplayValue && typeof fieldSchema.listDisplayValue === 'function') {
        const displayValue = fieldSchema.listDisplayValue(value, item, memoizedOptions, fieldSchema);
        return (
          <Typography variant='body2' sx={{ whiteSpace: 'pre-wrap' }}>
            {displayValue}
          </Typography>
        );
      }

      return (
        <EditableText
          value={value}
          setValue={newValue => handleFieldUpdate(key, newValue, item)}
          isEditable={isEditableField}
          type={fieldSchema.type}
          displayValue={
            fieldSchema.listDisplayValue || fieldSchema.displayValue
              ? displayValueValue =>
                  (fieldSchema.listDisplayValue || fieldSchema.displayValue)(
                    displayValueValue,
                    item,
                    memoizedOptions,
                    fieldSchema
                  )
              : undefined
          }
          label={isCardView ? null : fieldSchema.label}
          placeholder={fieldSchema.placeholder}
          schema={fieldSchema}
          required={fieldSchema.required}
          showValidation={true}
          clampLines={fieldSchema.previewInList ? fieldSchema.previewMaxLines || 2 : 0}
          hyphenate={Boolean(fieldSchema.useHyphens)}
          truncate={Number.isFinite(fieldSchema.truncate) ? fieldSchema.truncate : 0}
          truncateMiddle={Boolean(fieldSchema.truncateMiddle)}
        />
      );
    },
    [
      schema,
      memoizedOptions,
      canUpdate,
      inlineEditingDisabled,
      handleFieldUpdate,
      resourceType,
      itemId,
      onItemUpdate,
      filterOptionsByParent,
    ]
  );

  // column options for multi-select (optional virtual _id column for Mongo documents)
  const columnOptions = useMemo(() => {
    const base = Object.keys(schema).map(key => ({
      value: key,
      label: schema[key]?.label || key,
    }));
    if (hideMongoIdColumn) return base;
    return [...base, { value: ID_COLUMN_KEY, label: ID_COLUMN_KEY }];
  }, [schema, hideMongoIdColumn]);

  const filteredItems = useMemo(() => {
    if (!items) return [];
    if (serverSideListEnabled) return items;
    if (!currentSearchTerm.trim()) return items;

    const term = currentSearchTerm.trim().toLowerCase();
    return items.filter(item => {
      return Object.values(item).some(val => {
        if (val === null || val === undefined) return false;
        if (typeof val === 'object') {
          return JSON.stringify(val).toLowerCase().includes(term);
        }
        return String(val).toLowerCase().includes(term);
      });
    });
  }, [items, currentSearchTerm, serverSideListEnabled]);

  // Memoize visible columns for better performance; include virtual _id when selected
  const orderedVisibleColumns = useMemo(() => {
    const fromSchema = Object.keys(schema).filter(key => {
      const fieldSchema = schema[key];
      return visibleColumns.includes(key) && !fieldSchema?.excludeFromList;
    });
    if (hideMongoIdColumn || !visibleColumns.includes(ID_COLUMN_KEY)) return fromSchema;
    return [...fromSchema, ID_COLUMN_KEY];
  }, [schema, visibleColumns, hideMongoIdColumn]);

  // On mobile we expand cards to show every available field (not just the
  // desktop-restricted visible columns) so that no information is hidden.
  const mobileColumns = useMemo(() => {
    const fromSchema = Object.keys(schema).filter(key => !schema[key]?.excludeFromList);
    if (!hideMongoIdColumn && visibleColumns.includes(ID_COLUMN_KEY)) {
      return [...fromSchema, ID_COLUMN_KEY];
    }
    return fromSchema;
  }, [schema, hideMongoIdColumn, visibleColumns]);

  // Columns shown while a mobile card is collapsed. Prefer fields explicitly
  // flagged for preview, otherwise fall back to the first couple of columns.
  const mobilePreviewColumns = useMemo(() => {
    const flagged = mobileColumns.filter(key => schema[key]?.previewInList);
    if (flagged.length > 0) return flagged;
    const previewCount = Number.isFinite(Number(options?.mobilePreviewFieldCount))
      ? Math.max(0, Number(options.mobilePreviewFieldCount))
      : 2;
    const base = orderedVisibleColumns.length > 0 ? orderedVisibleColumns : mobileColumns;
    return base
      .filter(key => key !== ID_COLUMN_KEY && schema[key]?.type !== 'nested')
      .slice(0, previewCount);
  }, [mobileColumns, orderedVisibleColumns, schema, options?.mobilePreviewFieldCount]);

  // Remaining columns revealed when a mobile card is expanded.
  const mobileCollapsibleColumns = useMemo(
    () => mobileColumns.filter(key => !mobilePreviewColumns.includes(key)),
    [mobileColumns, mobilePreviewColumns]
  );

  const activeColumnFilterCount = useMemo(() => {
    if (!columnFilters || typeof columnFilters !== 'object') return 0;
    return Object.values(columnFilters).filter(f => f && typeof f === 'object' && f.op).length;
  }, [columnFilters]);

  const activeFilterEntries = useMemo(() => {
    if (!columnFilters || typeof columnFilters !== 'object') return [];
    return Object.entries(columnFilters)
      .map(([key, filter]) => ({ key, filter }))
      .filter(({ filter }) => filter && typeof filter === 'object' && filter.op);
  }, [columnFilters]);

  const getFilterableType = useCallback(fieldSchema => {
    const declaredType = fieldSchema?.filterType || fieldSchema?.sortType || fieldSchema?.type;
    if (declaredType === 'number' || declaredType === 'price') return 'number';
    if (declaredType === 'milliseconds') return 'number';
    if (declaredType === 'date') return 'date';
    if (declaredType === 'boolean') return 'boolean';
    if (declaredType === 'select') return 'select';
    if (declaredType === 'multi-select') return 'multi-select';
    if (declaredType === 'text' || declaredType === 'string') return 'string';
    if (declaredType === 'array') return 'string';
    return 'presence';
  }, []);

  const getFilterLabel = useCallback(
    (key, filter) => {
      const label = schema?.[key]?.label || key;
      const op = filter?.op;

      const opLabel =
        op === 'gt'
          ? '>'
          : op === 'gte'
            ? '≥'
            : op === 'lt'
              ? '<'
              : op === 'lte'
                ? '≤'
                : op === 'eq'
                  ? '='
                  : op === 'contains'
                    ? 'enthält'
                    : op === 'starts_with'
                      ? 'beginnt mit'
                      : op === 'ends_with'
                        ? 'endet mit'
                        : op === 'present'
                          ? 'vorhanden'
                          : op === 'not_present'
                            ? 'nicht vorhanden'
                            : op;

      if (op === 'present' || op === 'not_present') return `${label}: ${opLabel}`;

      const rawValue = filter?.value;
      const fieldSchema = schema?.[key] || {};
      const filterType = getFilterableType(fieldSchema);
      const optionSource = fieldSchema.api?.resource ? memoizedOptions?.[key] : fieldSchema.options;
      const optionList = Array.isArray(optionSource) ? optionSource : [];

      let valueLabel;
      if (typeof rawValue === 'boolean') {
        valueLabel = rawValue ? 'wahr' : 'falsch';
      } else if (filterType === 'select') {
        const found = optionList.find(opt => String(opt?.value) === String(rawValue));
        valueLabel = found ? found.label : String(rawValue ?? '');
      } else if (filterType === 'multi-select') {
        const selected = Array.isArray(rawValue) ? rawValue : rawValue ? [rawValue] : [];
        valueLabel = selected
          .map(v => {
            const found = optionList.find(opt => String(opt?.value) === String(v));
            return found ? found.label : String(v ?? '');
          })
          .filter(Boolean)
          .join(', ');
      } else if (filterType === 'date' && rawValue) {
        const dateMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(rawValue));
        if (dateMatch) {
          valueLabel = `${dateMatch[3]}.${dateMatch[2]}.${dateMatch[1]}`;
        } else {
          valueLabel = String(rawValue ?? '');
        }
      } else {
        valueLabel = String(rawValue ?? '');
      }

      return `${label}: ${opLabel} ${valueLabel}`.trim();
    },
    [schema, getFilterableType, memoizedOptions]
  );

  const isFilterableColumn = useCallback(
    key => {
      const fieldSchema = schema?.[key] || {};
      if (fieldSchema?.excludeFromList) return false;
      if (fieldSchema?.type === 'nested' || fieldSchema?.type === 'object') return false;
      return Boolean(getFilterableType(fieldSchema));
    },
    [schema, getFilterableType]
  );

  const openFilterMenu = useCallback(
    (key, event) => {
      const fieldSchema = schema?.[key] || {};
      const type = getFilterableType(fieldSchema);
      const currentFilter = columnFilters?.[key] || {};
      const op =
        currentFilter.op ||
        (type === 'boolean'
          ? 'eq'
          : type === 'select' || type === 'multi-select'
            ? 'eq'
          : type === 'string'
            ? 'contains'
            : type === 'presence'
              ? 'present'
              : 'gt');
      const value =
        type === 'multi-select'
          ? Array.isArray(currentFilter.value)
            ? currentFilter.value
            : currentFilter.value
              ? [currentFilter.value]
              : []
          : (currentFilter.value ?? '');

      setFilterMenu({
        anchorEl: event.currentTarget,
        key,
        draftOp: op,
        draftValue: value,
      });
    },
    [schema, getFilterableType, columnFilters]
  );

  const closeFilterMenu = useCallback(() => {
    setFilterMenu({ anchorEl: null, key: null, draftOp: null, draftValue: '' });
  }, []);

  const clearColumnFilter = useCallback(key => {
    setColumnFilters(prev => {
      const next = { ...(prev || {}) };
      delete next[key];
      return next;
    });
  }, []);

  const clearAllColumnFilters = useCallback(() => {
    setColumnFilters({});
  }, []);

  useEffect(() => {
    if (!serverSideFilteringEnabled) return;
    setCurrentPage(1);
  }, [serverSideFilteringEnabled, JSON.stringify(columnFilters)]);

  const handleRequestSort = useCallback(columnKey => {
    setSortConfig(prev => {
      if (prev.key === columnKey) {
        return { key: columnKey, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key: columnKey, direction: 'asc' };
    });
    if (serverSideSortingEnabled) {
      setCurrentPage(1);
    }
  }, [serverSideSortingEnabled]);

  useEffect(() => {
    if (sortConfig.key && !orderedVisibleColumns.includes(sortConfig.key)) {
      setSortConfig(prev => ({ ...prev, key: null }));
    }
  }, [orderedVisibleColumns, sortConfig.key]);

  const columnFilteredItems = useMemo(() => {
    if (!Array.isArray(filteredItems)) return [];
    if (serverSideFilteringEnabled) return filteredItems;
    if (!columnFilters || Object.keys(columnFilters).length === 0) return filteredItems;

    const isEmpty = v =>
      v === null || v === undefined || v === '' || (Array.isArray(v) && v.length === 0);

    const isPresent = v => {
      if (v === null || v === undefined) return false;
      if (typeof v === 'string') return v.trim().length > 0;
      if (Array.isArray(v)) return v.length > 0;
      if (typeof v === 'object') return Object.keys(v).length > 0;
      return true;
    };

    const parseNumber = v => {
      if (typeof v === 'number') return Number.isFinite(v) ? v : null;
      if (typeof v === 'boolean') return v ? 1 : 0;
      const raw = String(v ?? '').trim();
      if (!raw) return null;

      let s = raw.replace(/\s/g, '');
      s = s.replace(/[€$£]/g, '');
      s = s.replace(/[^0-9,.\-]/g, '');

      const hasComma = s.includes(',');
      const hasDot = s.includes('.');
      if (hasComma && hasDot) {
        s = s.replace(/\./g, '');
        s = s.replace(',', '.');
      } else if (hasComma && !hasDot) {
        s = s.replace(',', '.');
      }

      const n = Number.parseFloat(s);
      return Number.isFinite(n) ? n : null;
    };

    const parseDateTs = v => {
      if (v instanceof Date) {
        const t = v.getTime();
        return Number.isFinite(t) ? t : null;
      }
      const str = String(v);
      const dateOnlyMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(str);
      if (dateOnlyMatch) {
        const year = Number(dateOnlyMatch[1]);
        const month = Number(dateOnlyMatch[2]);
        const day = Number(dateOnlyMatch[3]);
        const t = new Date(year, month - 1, day, 12, 0, 0, 0).getTime();
        return Number.isFinite(t) ? t : null;
      }
      const t = Date.parse(str);
      return Number.isFinite(t) ? t : null;
    };

    const parseBoolean = v => {
      if (typeof v === 'boolean') return v;
      if (v === 1 || v === '1') return true;
      if (v === 0 || v === '0') return false;
      if (typeof v === 'string') {
        const s = v.trim().toLowerCase();
        if (s === 'true' || s === 'wahr' || s === 'ja' || s === 'yes') return true;
        if (s === 'false' || s === 'falsch' || s === 'nein' || s === 'no') return false;
      }
      return null;
    };

    const parseDateRangeFromInput = v => {
      if (v === null || v === undefined || v === '') return null;
      const s = String(v).trim();
      const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
      if (!m) return null;

      const year = Number(m[1]);
      const month = Number(m[2]);
      const day = Number(m[3]);

      const start = new Date(year, month - 1, day, 0, 0, 0, 0).getTime();
      const end = new Date(year, month - 1, day, 23, 59, 59, 999).getTime();
      if (!Number.isFinite(start) || !Number.isFinite(end)) return null;

      return { start, end };
    };

    const compare = (itemValue, filterOp, filterValue, type, fieldSchema) => {
      if (filterOp === 'present') return isPresent(itemValue);
      if (filterOp === 'not_present') return !isPresent(itemValue);
      if (type === 'presence') return true;

      // Collect every plausible scalar representation of a value. Subdocument
      // shapes like { _id, entertainmentId, name } carry their own Mongo _id,
      // so the schema-configured reference key (api.objectify.idProp /
      // api.idName) must also be considered — otherwise filtering by the
      // referenced id (the select option value) never matches.
      const collectScalarCandidates = v => {
        if (v === null || v === undefined) return [];
        if (typeof v !== 'object') return [String(v)];
        const candidateKeys = [
          fieldSchema?.api?.objectify?.idProp,
          fieldSchema?.api?.idName,
          '_id',
          'id',
          'value',
          'upgradeId',
          'resourceId',
          'name',
        ].filter(Boolean);
        const candidates = [];
        candidateKeys.forEach(key => {
          const candidate = v?.[key];
          if (candidate === null || candidate === undefined || candidate === '') return;
          if (typeof candidate === 'object') return;
          candidates.push(String(candidate));
        });
        if (candidates.length > 0) return candidates;
        try {
          return [JSON.stringify(v)];
        } catch {
          return [String(v)];
        }
      };

      if (type === 'select') {
        if (isEmpty(filterValue)) return true;
        const haystack = collectScalarCandidates(itemValue).map(v => v.toLowerCase());
        const needle = String(filterValue).toLowerCase();
        if (filterOp === 'eq') return haystack.some(v => v === needle);
        if (filterOp === 'contains') return haystack.some(v => v.includes(needle));
        if (filterOp === 'starts_with') return haystack.some(v => v.startsWith(needle));
        if (filterOp === 'ends_with') return haystack.some(v => v.endsWith(needle));
        return true;
      }

      if (type === 'multi-select') {
        if (isEmpty(filterValue)) return true;
        const values = Array.isArray(itemValue) ? itemValue : itemValue ? [itemValue] : [];
        const haystack = values.flatMap(v => collectScalarCandidates(v)).filter(Boolean);

        // When the filter supplies multiple values we treat it as "any of".
        if (Array.isArray(filterValue)) {
          const needles = filterValue.flatMap(v => collectScalarCandidates(v)).filter(Boolean);
          if (needles.length === 0) return true;
          return haystack.some(v => needles.some(n => String(v) === String(n)));
        }

        const needles = collectScalarCandidates(filterValue).filter(Boolean);
        if (needles.length === 0) return true;
        if (filterOp === 'eq') {
          return haystack.some(v => needles.some(n => String(v) === String(n)));
        }
        const needlesLc = needles.map(n => String(n).toLowerCase());
        return haystack.some(v => needlesLc.some(n => String(v).toLowerCase().includes(n)));
      }

      if (type === 'string') {
        if (isEmpty(filterValue)) return true;
        const haystack =
          itemValue === null || itemValue === undefined ? '' : String(itemValue).toLowerCase();
        const needle = String(filterValue).toLowerCase();
        if (filterOp === 'contains') return haystack.includes(needle);
        if (filterOp === 'starts_with') return haystack.startsWith(needle);
        if (filterOp === 'ends_with') return haystack.endsWith(needle);
        if (filterOp === 'eq') return haystack === needle;
        return true;
      }

      if (type === 'boolean') {
        const boolValue = parseBoolean(itemValue);
        const filterBool = parseBoolean(filterValue);
        if (filterOp === 'eq') return boolValue !== null && filterBool !== null && boolValue === filterBool;
        return true;
      }

      if (type === 'number') {
        if (isEmpty(filterValue)) return true;
        const a = parseNumber(itemValue);
        const b = parseNumber(filterValue);
        if (a === null || b === null) return false;
        if (filterOp === 'gt') return a > b;
        if (filterOp === 'gte') return a >= b;
        if (filterOp === 'lt') return a < b;
        if (filterOp === 'lte') return a <= b;
        if (filterOp === 'eq') return a === b;
        return true;
      }

      if (type === 'date') {
        if (isEmpty(filterValue)) return true;
        const a = parseDateTs(itemValue);
        const range = parseDateRangeFromInput(filterValue);
        if (a === null || !range) return false;
        if (filterOp === 'gt') return a > range.end;
        if (filterOp === 'gte') return a >= range.start;
        if (filterOp === 'lt') return a < range.start;
        if (filterOp === 'lte') return a <= range.end;
        if (filterOp === 'eq') return a >= range.start && a <= range.end;
        return true;
      }

      return true;
    };

    return filteredItems.filter(item => {
      for (const [key, filter] of Object.entries(columnFilters)) {
        if (!filter || typeof filter !== 'object' || !filter.op) continue;
        const fieldSchema = schema?.[key] || {};
        const type = getFilterableType(fieldSchema);
        if (!type) continue;

        let rawValue =
          typeof fieldSchema?.filterValue === 'function'
            ? fieldSchema.filterValue(item?.[key], item)
            : item?.[key];

        if (fieldSchema.type === 'milliseconds') {
          const n = typeof rawValue === 'number' ? rawValue : Number(rawValue);
          rawValue = Number.isFinite(n) ? n / 60000 : rawValue;
        }

        if (!compare(rawValue, filter.op, filter.value, type, fieldSchema)) return false;
      }
      return true;
    });
  }, [filteredItems, columnFilters, schema, getFilterableType, serverSideFilteringEnabled]);

  const sortedItems = useMemo(() => {
    if (!Array.isArray(columnFilteredItems)) return [];
    if (!sortConfig?.key) return columnFilteredItems;
    if (serverSideSortingEnabled) return columnFilteredItems;

    const key = sortConfig.key;
    const direction = sortConfig.direction === 'desc' ? 'desc' : 'asc';
    const fieldSchema = schema?.[key] || {};
    const collator = new Intl.Collator('de', { numeric: true, sensitivity: 'base' });

    const getSortValue = item => {
      const rawValue = item?.[key];

      if (typeof fieldSchema?.sortValue === 'function') {
        return fieldSchema.sortValue(rawValue, item);
      }

      if (typeof fieldSchema?.listDisplayValue === 'function') {
        return fieldSchema.listDisplayValue(rawValue, item, memoizedOptions, fieldSchema);
      }

      return rawValue;
    };

    const normalize = v => {
      const isEmpty = v === null || v === undefined || v === '';
      if (isEmpty) return { empty: true, type: 'empty', value: null };

      const declaredType = fieldSchema?.sortType || fieldSchema?.type;

      if (declaredType === 'presence') {
        return { empty: false, type: 'presence', value: 1 };
      }

      if (declaredType === 'boolean' || typeof v === 'boolean') {
        return { empty: false, type: 'boolean', value: v ? 1 : 0 };
      }

      if (declaredType === 'milliseconds') {
        const n = typeof v === 'number' ? v : Number(v);
        if (Number.isFinite(n)) return { empty: false, type: 'number', value: n / 60000 };
      }

      if (declaredType === 'number' || typeof v === 'number') {
        const n = typeof v === 'number' ? v : Number(v);
        if (Number.isFinite(n)) return { empty: false, type: 'number', value: n };
      }

      if (declaredType === 'date' || declaredType === 'datetime' || declaredType === 'dayjs') {
        const t = v instanceof Date ? v.getTime() : Date.parse(String(v));
        if (Number.isFinite(t)) return { empty: false, type: 'date', value: t };
      }

      if (Array.isArray(v)) {
        return { empty: false, type: 'string', value: v.join(', ') };
      }

      if (typeof v === 'object') {
        try {
          return { empty: false, type: 'string', value: JSON.stringify(v) };
        } catch {
          return { empty: false, type: 'string', value: String(v) };
        }
      }

      return { empty: false, type: 'string', value: String(v) };
    };

    const compareNormalized = (a, b) => {
      if (a.empty && b.empty) return 0;
      if (a.empty && !b.empty) return 1;
      if (!a.empty && b.empty) return -1;

      const type = a.type === b.type ? a.type : 'string';
      if (type === 'number' || type === 'date' || type === 'boolean' || type === 'presence') {
        return a.value - b.value;
      }

      return collator.compare(String(a.value), String(b.value));
    };

    return columnFilteredItems
      .map((item, idx) => ({ item, idx, value: normalize(getSortValue(item)) }))
      .sort((a, b) => {
        const result = compareNormalized(a.value, b.value);
        if (result !== 0) return direction === 'asc' ? result : -result;
        return a.idx - b.idx;
      })
      .map(entry => entry.item);
  }, [columnFilteredItems, schema, sortConfig, serverSideSortingEnabled]);

  // Shared server/external pagination model used by both the table and card views.
  const paginationModel = useMemo(() => {
    if (externalPagination && typeof externalPagination === 'object') {
      const page = Number(externalPagination.page);
      const limit = Number(externalPagination.limit);
      const total = Number(externalPagination.total);
      if (!Number.isFinite(page) || !Number.isFinite(limit) || !Number.isFinite(total)) return null;
      if (typeof externalPagination.onChange !== 'function') return null;
      return {
        page,
        limit,
        total,
        totalIsCapped: Boolean(externalPagination.totalIsCapped),
        onChange: externalPagination.onChange,
        rowsPerPageOptions: externalPagination.rowsPerPageOptions,
      };
    }
    if (!nested && pagination) {
      return {
        page: pagination.page || currentPage,
        limit: pagination.limit || itemsPerPage,
        total: pagination.total || 0,
        totalIsCapped: Boolean(pagination.totalIsCapped),
        onChange: ({ page, limit }) => {
          if (typeof limit === 'number' && limit !== itemsPerPage) setItemsPerPage(limit);
          if (typeof page === 'number' && page !== currentPage) setCurrentPage(page);
        },
        rowsPerPageOptions: [10, 20, 25, 50, 100],
      };
    }
    return null;
  }, [externalPagination, nested, pagination, currentPage, itemsPerPage]);

  // Client-side pagination for the mobile card view. Only used when there is no
  // server/external pagination (e.g. nested lists), so long lists are not all
  // rendered at once on small screens.
  const cardClientPaginationActive =
    isCardView && !paginationModel && Array.isArray(sortedItems) && sortedItems.length > cardPageSize;
  const cardPageCount = cardClientPaginationActive
    ? Math.max(1, Math.ceil(sortedItems.length / cardPageSize))
    : 1;
  const effectiveCardPage = Math.min(Math.max(1, cardPage), cardPageCount);
  const cardVisibleItems = useMemo(() => {
    if (!Array.isArray(sortedItems)) return [];
    if (!cardClientPaginationActive) return sortedItems;
    const start = (effectiveCardPage - 1) * cardPageSize;
    return sortedItems.slice(start, start + cardPageSize);
  }, [sortedItems, cardClientPaginationActive, effectiveCardPage, cardPageSize]);

  // Reset the mobile page when the underlying result set changes.
  useEffect(() => {
    setCardPage(1);
  }, [currentSearchTerm, JSON.stringify(columnFilters), sortConfig.key, sortConfig.direction]);

  // Memoize the table rows to prevent unnecessary re-renders
  const tableRows = useMemo(() => {
    const firstDataKey = orderedVisibleColumns[0];
    return sortedItems?.map((item, index) => (
      <TableRow
        key={(item?._id ? `${item._id}` : 'item') + `-${index}`}
        onClick={e => {
          // Check if the click target is an editable field
          const target = e.target;
          const isEditableField =
            target.closest('.MuiTextField-root') ||
            target.closest('.MuiSelect-root') ||
            target.closest('.MuiChip-root') ||
            target.closest('.MuiButton-root');

          if (!isEditableField) {
            handleItemSelect(item);
            onRowClick && onRowClick(item);
          }
        }}
        sx={{
          cursor: 'pointer',
          backgroundColor: getZebraRowBackground(index),
          '&:hover': { backgroundColor: rowHoverOpaqueBackground },
          '&:hover .fz-sticky-cell': { backgroundColor: rowHoverOpaqueBackground },
          ...(typeof getRowSx === 'function' ? getRowSx(item, index) || {} : {}),
        }}
      >
        {selectionEnabled && (
          <TableCell
            padding='checkbox'
            onClick={e => e.stopPropagation()}
            sx={{
              ...stickyBodyCellSx,
              left: stickyLeftConfig.selectionLeft,
              width: stickyLeftConfig.selectionWidth,
              minWidth: stickyLeftConfig.selectionWidth,
              maxWidth: stickyLeftConfig.selectionWidth,
              backgroundColor: getZebraRowBackground(index),
              backgroundImage: 'none',
            }}
            className='fz-sticky-cell'
          >
            <Checkbox
              checked={selectionSelectedIds.includes(getSelectionRowId(item, index))}
              onChange={e => toggleSelectionRow(getSelectionRowId(item, index), e.target.checked)}
            />
          </TableCell>
        )}
        {(!isSingleObjectListContext && canCreate) && (
          <TableCell
            padding='checkbox'
            sx={{
              ...stickyBodyCellSx,
              left: stickyLeftConfig.createLeft,
              width: stickyLeftConfig.createWidth,
              minWidth: stickyLeftConfig.createWidth,
              maxWidth: stickyLeftConfig.createWidth,
              whiteSpace: 'nowrap',
              backgroundColor: getZebraRowBackground(index),
              backgroundImage: 'none',
            }}
            className='fz-sticky-cell'
          />
        )}
        {detailViewEnabled && (
          <TableCell
            padding='checkbox'
            onClick={e => {
              e.stopPropagation();
              handleOpenDetailView(item);
            }}
            sx={{
              ...stickyBodyCellSx,
              left: stickyLeftConfig.detailOpenLeft,
              width: stickyLeftConfig.detailOpenWidth,
              minWidth: stickyLeftConfig.detailOpenWidth,
              maxWidth: stickyLeftConfig.detailOpenWidth,
              whiteSpace: 'nowrap',
              backgroundColor: getZebraRowBackground(index),
              backgroundImage: 'none',
            }}
            className='fz-sticky-cell'
          >
            <Tooltip title='Details'>
              <IconButton size='small'>
                <OpenInNewIcon fontSize='small' />
              </IconButton>
            </Tooltip>
          </TableCell>
        )}
        {hasLeadingRowAction && (
          <TableCell
            padding='checkbox'
            onClick={e => {
              e.stopPropagation();
            }}
            sx={{
              ...stickyBodyCellSx,
              left: stickyLeftConfig.leadingActionLeft,
              width: stickyLeftConfig.leadingActionWidth,
              minWidth: stickyLeftConfig.leadingActionWidth,
              maxWidth: stickyLeftConfig.leadingActionWidth,
              whiteSpace: 'nowrap',
              backgroundColor: getZebraRowBackground(index),
              backgroundImage: 'none',
            }}
            className='fz-sticky-cell'
          >
            {(() => {
              const cfg = leadingRowAction(item);
              if (!cfg || typeof cfg !== 'object') return null;
              const { icon, tooltip, onClick, disabled, right } = cfg;
              if (!icon || typeof onClick !== 'function') return null;
              const btn = (
                <IconButton
                  size='small'
                  onClick={e => {
                    e.stopPropagation();
                    onClick(item);
                  }}
                  disabled={Boolean(disabled)}
                >
                  {right ? <LockIconWrapper right={right}>{icon}</LockIconWrapper> : icon}
                </IconButton>
              );
              if (typeof tooltip === 'string' && tooltip.trim()) {
                return <Tooltip title={tooltip}>{btn}</Tooltip>;
              }
              return btn;
            })()}
          </TableCell>
        )}
        {orderedVisibleColumns.map(key => {
          const stableId = item?._id || `${index}`;
          const cellKey = `${stableId}-${key}`;
          const fieldSchema = schema[key];
          const isFirstDataCol = key === firstDataKey;

          if (key === ID_COLUMN_KEY) {
            return (
              <TableCell
                key={cellKey}
                sx={{
                  verticalAlign: 'top',
                  ...(isFirstDataCol
                    ? {
                        ...stickyBodyCellSx,
                        ...stickyFirstDataEdgeSx,
                        left: stickyLeftConfig.firstDataLeft,
                        zIndex: nested ? 3 : 19,
                        borderRight: '1px solid',
                        borderColor: 'divider',
                        backgroundColor: getZebraRowBackground(index),
                        backgroundImage: 'none',
                      }
                    : null),
                }}
                className={isFirstDataCol ? 'fz-sticky-cell' : undefined}
              >
                <Typography variant='body2' sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                  {item?._id != null ? String(item._id) : '—'}
                </Typography>
              </TableCell>
            );
          }

          if (fieldSchema?.listDisplayValue && item?.[key] !== undefined) {
            return (
              <TableCell
                key={cellKey}
                sx={{
                  verticalAlign: 'top',
                  ...(isFirstDataCol
                    ? {
                        ...stickyBodyCellSx,
                        ...stickyFirstDataEdgeSx,
                        left: stickyLeftConfig.firstDataLeft,
                        zIndex: nested ? 3 : 19,
                        borderRight: '1px solid',
                        borderColor: 'divider',
                        backgroundColor: getZebraRowBackground(index),
                        backgroundImage: 'none',
                      }
                    : null),
                }}
                className={isFirstDataCol ? 'fz-sticky-cell' : undefined}
              >
                {renderField(key, item?.[key], item)}
              </TableCell>
            );
          }

          return (
            <TableCell
              key={cellKey}
              sx={{
                verticalAlign: 'top',
                ...(isFirstDataCol
                  ? {
                      ...stickyBodyCellSx,
                      ...stickyFirstDataEdgeSx,
                      left: stickyLeftConfig.firstDataLeft,
                      zIndex: nested ? 3 : 19,
                      borderRight: '1px solid',
                      borderColor: 'divider',
                      backgroundColor: getZebraRowBackground(index),
                      backgroundImage: 'none',
                    }
                  : null),
              }}
              className={isFirstDataCol ? 'fz-sticky-cell' : undefined}
            >
              {fieldSchema?.type === 'nested'
                ? renderNestedContent(item[key], key, item?._id)
                : fieldSchema?.type === 'object'
                  ? renderObjectContent(item[key], key, item?._id)
                  : renderField(key, item?.[key], item)}
            </TableCell>
          );
        })}
        {showTrailingActions && (
          <TableCell align='right' sx={{ whiteSpace: 'nowrap' }}>
            {typeof renderRowActions === 'function' && (
              <Box
                component='span'
                sx={{ mr: canDelete || canDuplicate ? 1 : 0, whiteSpace: 'nowrap' }}
                onClick={e => e.stopPropagation()}
              >
                {renderRowActions(item)}
              </Box>
            )}
            {canDuplicate && (
              <Tooltip title='Duplizieren'>
                <IconButton
                  color='primary'
                  onClick={e => {
                    e.stopPropagation();
                    handleDuplicateItem(item);
                  }}
                  size='small'
                  sx={{ borderRadius: 1, whiteSpace: 'nowrap', mr: canDelete ? 0.5 : 0 }}
                >
                  <LockIconWrapper right={`${rightsResourceType}:create`}>
                    <ContentCopyIcon fontSize='small' />
                  </LockIconWrapper>
                </IconButton>
              </Tooltip>
            )}
            {canDelete && (
              <Tooltip title='Löschen'>
                <IconButton
                  color='error'
                  onClick={e => {
                    e.stopPropagation();
                    handleDeleteClick(item?._id, 'diesen Eintrag'); // TODO itemname?
                  }}
                  size='small'
                  sx={{ borderRadius: 1, whiteSpace: 'nowrap' }}
                >
                  <LockIconWrapper right={`${rightsResourceType}:delete`}>
                    <DeleteIcon fontSize='small' />
                  </LockIconWrapper>
                </IconButton>
              </Tooltip>
            )}
          </TableCell>
        )}
      </TableRow>
    ));
  }, [
    sortedItems,
    orderedVisibleColumns,
    expandedItems,
    canUpdate,
    canDelete,
    canCreate,
    canDuplicate,
    handleItemSelect,
    handleDeleteClick,
    handleDuplicateItem,
    nested,
    renderNestedContent,
    renderField,
    renderObjectContent,
    showTrailingActions,
    isSingleObjectListContext,
    theme,
    onRowClick,
    selectionEnabled,
    selectionSelectedIds,
    getSelectionRowId,
    toggleSelectionRow,
    hasLeadingRowAction,
    leadingRowAction,
    detailViewEnabled,
    handleOpenDetailView,
    rightsResourceType,
    stickyLeftConfig,
    stickyBodyCellSx,
    stickyFirstDataEdgeSx,
    getZebraRowBackground,
    rowHoverOpaqueBackground,
    getRowSx,
  ]);

  const mainListContent = useMemo(() => {
    // ──────────────────────────────────────────────────────────────────────────────
    // MOBILE NESTED VIEW
    // ──────────────────────────────────────────────────────────────────────────────
    if (isCardView && mobileNestedContext) {
      const { parentId, path } = mobileNestedContext;
      const parentItem = items?.find(it => it._id === parentId);
      const nestedData = parentItem ? parentItem[path] : [];
      const nestedSchema = schema[path]?.schema || {};

      return (
        <Box>
          <Box p={2} display='flex' alignItems='center' gap={2} flexWrap='wrap'>
            <Button
              startIcon={<NavigateBeforeIcon />}
              variant='outlined'
              onClick={exitMobileNested}
              sx={{ textTransform: 'none' }}
            >
              Zurück
            </Button>
            {
              <Typography
                variant='h6'
                sx={{
                  fontSize: '1rem',
                  fontWeight: 600,
                  minWidth: 100,
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis',
                  flex: 1,
                }}
              >
                {schema[path]?.label || path}
              </Typography>
            }
          </Box>
          <EditableList
            key={`${parentId}-${path}`}
            fieldSchema={schema[path]}
            schema={nestedSchema}
            title={(() => {
              const fieldSchema = schema[path];
              return fieldSchema?.addButtonText || `${fieldSchema?.label || path}`;
            })()}
            nested={true}
            resourceType={resourceType}
            path={path}
            parentIsEditable={canUpdate}
            data={nestedData}
            parentDataItem={parentItem}
            itemId={parentId}
            onItemAdd={response => addNestedItemToParent(parentId, path, response)}
            onItemUpdate={response => updateNestedItemInParent(parentId, path, response)}
            onItemDelete={deletedId => deleteNestedItemFromParent(parentId, path, deletedId)}
            nestedBasePath={(() => {
              const field = schema[path];
              if (!field?.api?.nestedEndpoint) return null;
              const endpoint = field.api.nestedEndpoint;
              const parentIsSingleObject = Boolean(field?.singleObject);
              const base = nestedBasePath
                ? parentIsSingleObject
                  ? `${nestedBasePath}`
                  : `${nestedBasePath}/${parentId}`
                : `/api/v1/${resourceType}/${parentId}`;
              return `${base}/${endpoint}`;
            })()}
          />
        </Box>
      );
    }
    // ──────────────────────────────────────────────────────────────────────────────
    // CARD VIEW (≤ md)
    // ──────────────────────────────────────────────────────────────────────────────
    if (isCardView) {
      return (
        <Box sx={{ position: 'relative' }}>
          {/* Background refetch indicator; the previous items stay visible */}
          {isLoading && items !== null && (
            <LinearProgress
              sx={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 30 }}
            />
          )}
          {/* Top-bar */}
          <Box
            p={isXsDown ? 1 : 2}
            display='flex'
            flexDirection={isXsDown ? 'column' : 'row'}
            flexWrap='wrap'
            alignItems={isXsDown ? 'stretch' : 'flex-start'}
            gap={isXsDown ? 1 : 2}
          >
            {typeof renderTopControlsStart === 'function'
              ? renderTopControlsStart({
                  isCardView,
                  isXsDown,
                  isLargeDesktop,
                  isMidDesktop,
                  canCreate,
                  canUpdate,
                  canDelete,
                  visibleColumns,
                })
              : null}
            {!isSingleObjectListContext && canCreate && (
              <Button
                variant='contained'
                color='success'
                size='small'
                startIcon={<AddIcon />}
                onClick={handleOpenDialog}
                sx={{ minWidth: 'auto', px: 2, whiteSpace: 'nowrap', height: isXsDown ? 36 : 40 }}
              >
                <LockIconWrapper right={`${rightsResourceType}:create`}>{addLabel}</LockIconWrapper>
              </Button>
            )}

            {!isSingleObjectListContext && Boolean(!hideSearchField) && (
              <TextField
                placeholder={activeSearchTerm || searchPlaceholder}
                ref={searchInputRef}
                defaultValue=''
                onKeyDown={handleSearchKeyDown}
                sx={{
                  ...searchFieldStyles,
                  '& .MuiInputBase-input': {
                    height: isXsDown ? 36 : 40,
                    padding: isXsDown ? '0 12px' : '0 14px',
                    fontSize: isXsDown ? '0.875rem' : '1rem',
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>
                      <SearchIcon
                        sx={{ color: 'text.secondary', fontSize: isXsDown ? '1.2rem' : '1.5rem' }}
                      />
                    </InputAdornment>
                  ),
                }}
              />
            )}
            {!isSingleObjectListContext && !hideColumnSelector && (
              <Select
                multiple
                value={visibleColumns}
                onChange={e => setVisibleColumns(e.target.value)}
                renderValue={() => 'Spalten auswählen'}
                sx={{
                  height: 40,
                  minWidth: 0,
                  flexShrink: 1,
                  '& .MuiSelect-select': {
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0 14px',
                    minHeight: '40px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  },
                }}
              >
                {columnOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    <Checkbox checked={visibleColumns.indexOf(option.value) > -1} />
                    <ListItemText primary={option.label} />
                  </MenuItem>
                ))}
              </Select>
            )}
            {typeof renderTopControlsEnd === 'function'
              ? renderTopControlsEnd({
                  isCardView,
                  isXsDown,
                  isLargeDesktop,
                  isMidDesktop,
                  canCreate,
                  canUpdate,
                  canDelete,
                  visibleColumns,
                })
              : null}
          </Box>

          {activeFilterEntries.length > 0 && (
            <Box sx={{ px: isXsDown ? 1 : 2, pb: isXsDown ? 1 : 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {activeFilterEntries.map(({ key, filter }) => (
                <Chip
                  key={key}
                  size='small'
                  label={getFilterLabel(key, filter)}
                  onDelete={() => clearColumnFilter(key)}
                />
              ))}
              <Chip
                size='small'
                icon={<ClearIcon />}
                label='Alle löschen'
                onClick={clearAllColumnFilters}
                variant='outlined'
              />
            </Box>
          )}

          {/* Content — the spinner replaces the list only when there are no rows to keep visible */}
          {items === null || (isLoading && sortedItems.length === 0) ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                p: 4,
                minHeight: 200,
              }}
            >
              <CircularProgress color='primary' />
            </Box>
          ) : sortedItems.length > 0 ? (
            <Box
              p={isXsDown ? 1 : 2}
              sx={isLoading ? { opacity: 0.55, pointerEvents: 'none' } : undefined}
            >
              <Grid container spacing={isXsDown ? 1 : 2}>
                {cardVisibleItems.map((item, index) => {
                  const rowId = item?._id ?? `idx-${index}`;
                  const titleKey = options?.titleKey;
                  const titleValue = titleKey ? item?.[titleKey] : item?.username || item?.name;
                  const isExpanded = Boolean(expandedCards[rowId]);
                  const hasCollapsible = mobileCollapsibleColumns.length > 0;
                  const hasHeaderActions =
                    selectionEnabled ||
                    hasLeadingRowAction ||
                    typeof renderRowActions === 'function' ||
                    (showTrailingActions && (canDuplicate || canDelete));
                  const showHeader = Boolean(titleValue) || hasCollapsible || hasHeaderActions;

                  const renderCardField = key => (
                    <Box
                      key={key}
                      display='flex'
                      flexDirection='column'
                      gap={isXsDown ? 0 : 0.25}
                      mb={isXsDown ? 0 : 0.5}
                    >
                      <Typography
                        variant='caption'
                        color='text.secondary'
                        sx={{ fontSize: isXsDown ? '0.7rem' : '0.75rem' }}
                      >
                        {key === ID_COLUMN_KEY ? ID_COLUMN_KEY : schema[key]?.label || key}
                      </Typography>
                      {key === ID_COLUMN_KEY ? (
                        <Typography
                          variant='body2'
                          sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
                        >
                          {item?._id != null ? String(item._id) : '—'}
                        </Typography>
                      ) : schema[key]?.type === 'nested' ? (
                        renderNestedContent(item[key], key, item?._id)
                      ) : schema[key]?.type === 'object' ? (
                        renderObjectContent(item[key], key, item?._id)
                      ) : (
                        renderField(key, item?.[key], item)
                      )}
                    </Box>
                  );

                  return (
                    <Grid item xs={12} sm={6} md={4} key={item?._id || `item-${index}`}>
                      <Paper
                        onClick={e => {
                          const target = e.target;
                          const isInteractive =
                            target.closest('.MuiTextField-root') ||
                            target.closest('.MuiSelect-root') ||
                            target.closest('.MuiChip-root') ||
                            target.closest('.MuiButton-root') ||
                            target.closest('.MuiIconButton-root') ||
                            target.closest('a') ||
                            target.closest('button') ||
                            target.closest('input');
                          if (!isInteractive) {
                            handleItemSelect(item);
                            onRowClick && onRowClick(item);
                          }
                        }}
                        sx={{
                          p: isXsDown ? 1 : 1.5,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: isXsDown ? 0.5 : 0.75,
                          height: '100%',
                        }}
                      >
                        {/* Header */}
                        {showHeader && (
                          <Box
                            display='flex'
                            justifyContent='space-between'
                            alignItems='center'
                            gap={0.5}
                            mb={isXsDown ? 0.25 : 0.5}
                            sx={{ minHeight: isXsDown ? 32 : 36 }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                minWidth: 0,
                                flex: 1,
                              }}
                            >
                              {hasCollapsible && (
                                <IconButton
                                  size='small'
                                  onClick={e => {
                                    e.stopPropagation();
                                    toggleCardExpand(rowId);
                                  }}
                                  sx={{ p: 0.25 }}
                                  aria-label={isExpanded ? 'Weniger anzeigen' : 'Mehr anzeigen'}
                                >
                                  {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                </IconButton>
                              )}
                              {titleValue ? (
                                <Typography
                                  variant='body4'
                                  fontWeight={600}
                                  textAlign='left'
                                  sx={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {titleValue}
                                </Typography>
                              ) : null}
                            </Box>
                            <Box
                              sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                              onClick={e => e.stopPropagation()}
                            >
                              {selectionEnabled && (
                                <Checkbox
                                  checked={selectionSelectedIds.includes(
                                    getSelectionRowId(item, index)
                                  )}
                                  onClick={e => e.stopPropagation()}
                                  onChange={e =>
                                    toggleSelectionRow(getSelectionRowId(item, index), e.target.checked)
                                  }
                                  size={isXsDown ? 'small' : 'medium'}
                                />
                              )}
                              {hasLeadingRowAction &&
                                (() => {
                                  const cfg = leadingRowAction(item);
                                  if (!cfg || typeof cfg !== 'object') return null;
                                  const { icon, tooltip, onClick, disabled, right } = cfg;
                                  if (!icon || typeof onClick !== 'function') return null;
                                  const btn = (
                                    <IconButton
                                      size={isXsDown ? 'small' : 'medium'}
                                      onClick={e => {
                                        e.stopPropagation();
                                        onClick(item);
                                      }}
                                      disabled={Boolean(disabled)}
                                      sx={{ padding: isXsDown ? 0.5 : 1 }}
                                    >
                                      {right ? <LockIconWrapper right={right}>{icon}</LockIconWrapper> : icon}
                                    </IconButton>
                                  );
                                  if (typeof tooltip === 'string' && tooltip.trim()) {
                                    return <Tooltip title={tooltip}>{btn}</Tooltip>;
                                  }
                                  return btn;
                                })()}
                              {typeof renderRowActions === 'function' && (
                                <Box onClick={e => e.stopPropagation()} sx={{ whiteSpace: 'nowrap' }}>
                                  {renderRowActions(item)}
                                </Box>
                              )}
                              {showTrailingActions && canDuplicate && (
                                <IconButton
                                  color='primary'
                                  onClick={e => {
                                    e.stopPropagation();
                                    handleDuplicateItem(item);
                                  }}
                                  size={isXsDown ? 'small' : 'medium'}
                                  sx={{ padding: isXsDown ? 0.5 : 1 }}
                                >
                                  <LockIconWrapper right={`${rightsResourceType}:create`}>
                                    <ContentCopyIcon fontSize={isXsDown ? 'small' : 'medium'} />
                                  </LockIconWrapper>
                                </IconButton>
                              )}
                              {showTrailingActions && canDelete && (
                                <IconButton
                                  color='error'
                                  onClick={e => {
                                    e.stopPropagation();
                                    handleDeleteClick(item?._id, 'diesen Eintrag');
                                  }}
                                  size={isXsDown ? 'small' : 'medium'}
                                  sx={{ padding: isXsDown ? 0.5 : 1 }}
                                >
                                  <LockIconWrapper right={`${rightsResourceType}:delete`}>
                                    <DeleteIcon fontSize={isXsDown ? 'small' : 'medium'} />
                                  </LockIconWrapper>
                                </IconButton>
                              )}
                            </Box>
                          </Box>
                        )}

                        {/* Preview fields (always visible) */}
                        {mobilePreviewColumns.map(key => renderCardField(key))}

                        {/* Collapsible fields */}
                        {hasCollapsible && (
                          <Collapse in={isExpanded} unmountOnExit>
                            <Box
                              sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: isXsDown ? 0 : 0.25,
                                mt: isXsDown ? 0.25 : 0.5,
                              }}
                            >
                              {mobileCollapsibleColumns.map(key => renderCardField(key))}
                            </Box>
                          </Collapse>
                        )}

                        {/* Expand / collapse toggle */}
                        {hasCollapsible && (
                          <Button
                            size='small'
                            variant='text'
                            onClick={e => {
                              e.stopPropagation();
                              toggleCardExpand(rowId);
                            }}
                            sx={{
                              alignSelf: 'flex-start',
                              textTransform: 'none',
                              mt: 0.25,
                              px: 0.5,
                              minWidth: 0,
                            }}
                          >
                            {isExpanded
                              ? 'Weniger anzeigen'
                              : `Mehr anzeigen (${mobileCollapsibleColumns.length})`}
                          </Button>
                        )}
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          ) : (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                p: 4,
                minHeight: 200,
                gap: 2,
              }}
            >
              <PlaylistRemoveIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
              <Typography variant='body1' color='text.secondary'>
                {currentSearchTerm
                  ? 'Keine Einträge für diese Suche gefunden.'
                  : 'Keine Einträge vorhanden.'}
              </Typography>
            </Box>
          )}

          {(() => {
            // Prefer server/external pagination; otherwise fall back to client-side
            // pagination so long mobile lists (incl. nested) are not rendered at once.
            const model = paginationModel
              ? paginationModel
              : cardClientPaginationActive
                ? {
                    page: effectiveCardPage,
                    limit: cardPageSize,
                    total: sortedItems.length,
                    totalIsCapped: false,
                    onChange: ({ page, limit }) => {
                      if (typeof limit === 'number' && limit > 0 && limit !== cardPageSize) {
                        setCardPageSize(limit);
                        setCardPage(1);
                        return;
                      }
                      if (typeof page === 'number') setCardPage(page);
                    },
                    rowsPerPageOptions: [10, 20, 50, 100],
                  }
                : null;

            if (!model) return null;
            const totalPages = model.limit > 0 ? Math.ceil(model.total / model.limit) : 0;
            const shouldShow = totalPages > 1;
            if (!shouldShow) return null;

            return (
              <Box sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
                <TablePagination
                  component='div'
                  count={model.total}
                  page={Math.max(0, model.page - 1)}
                  onPageChange={(e, p) => model.onChange({ page: p + 1, limit: model.limit })}
                  rowsPerPage={model.limit}
                  onRowsPerPageChange={e => {
                    const next = Number.parseInt(e.target.value, 10);
                    if (!Number.isFinite(next) || next <= 0) return;
                    model.onChange({ page: 1, limit: next });
                  }}
                  rowsPerPageOptions={model.rowsPerPageOptions || [10, 20, 25, 50, 100]}
                  labelRowsPerPage='Einträge:'
                  labelDisplayedRows={({ from, to, count }) =>
                    model.totalIsCapped
                      ? `${from}-${to} von mehr als ${count}`
                      : `${from}-${to} von ${count}`
                  }
                />
              </Box>
            );
          })()}
        </Box>
      );
    }

    // ──────────────────────────────────────────────────────────────────────────────
    // TABLE VIEW (> md)
    // ──────────────────────────────────────────────────────────────────────────────
    const TopControlsBase = (
      <>
        {typeof renderTopControlsStart === 'function'
          ? renderTopControlsStart({
              isCardView,
              isXsDown,
              isLargeDesktop,
              isMidDesktop,
              canCreate,
              canUpdate,
              canDelete,
              visibleColumns,
            })
          : null}
        {!isSingleObjectListContext && Boolean(!hideSearchField) && (
          <TextField
            placeholder={activeSearchTerm || searchPlaceholder}
            ref={searchInputRef}
            defaultValue=''
            onKeyDown={handleSearchKeyDown}
            sx={searchFieldStyles}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <SearchIcon sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
          />
        )}
        {typeof renderTopControlsEnd === 'function'
          ? renderTopControlsEnd({
              isCardView,
              isXsDown,
              isLargeDesktop,
              isMidDesktop,
              canCreate,
              canUpdate,
              canDelete,
              visibleColumns,
            })
          : null}
      </>
    );

    const selectionHeaderState = (() => {
      if (!selectionEnabled) return { checked: false, indeterminate: false };
      const ids = (sortedItems || []).map((it, idx) => getSelectionRowId(it, idx));
      const selectedVisible = ids.filter(id => selectionSelectedIds.includes(id)).length;
      return {
        checked: ids.length > 0 && selectedVisible === ids.length,
        indeterminate: selectedVisible > 0 && selectedVisible < ids.length,
      };
    })();

    const showInlineSearchField = !isSingleObjectListContext && Boolean(!hideSearchField);
    const hasCustomTopControls =
      typeof renderTopControlsStart === 'function' || typeof renderTopControlsEnd === 'function';
    const hasControlsRow = hasCustomTopControls || showInlineSearchField;
    const hasOnlyFilterChips = activeFilterEntries.length > 0 && !hasControlsRow;
    const hasAnyTopControls = hasControlsRow || activeFilterEntries.length > 0;

    const headerAddTool = !isSingleObjectListContext && canCreate ? (
      <Tooltip title={addLabel} arrow>
        <IconButton
          size='medium'
          color='success'
          onClick={e => {
            e.stopPropagation();
            handleOpenDialog();
          }}
          sx={{ p: 0.5 }}
        >
          <LockIconWrapper right={`${rightsResourceType}:create`}>
            <AddIcon fontSize='medium' />
          </LockIconWrapper>
        </IconButton>
      </Tooltip>
    ) : null;

    const headerColumnTool = ColumnSelectorTrigger;

    const firstOrderedVisibleColumnKey = orderedVisibleColumns[0];
    const lastOrderedVisibleColumnKey = orderedVisibleColumns[orderedVisibleColumns.length - 1];

    return (
      <Paper sx={tablePaperSx}>
        {/* Background refetch indicator; the previous items stay visible */}
        {isLoading && items !== null && (
          <LinearProgress
            sx={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 30 }}
          />
        )}
        {/* ── Top Controls Wrapper ── */}
        {hasAnyTopControls && (
          <>
            {hasOnlyFilterChips ? (
              <Box
                sx={{
                  px: 2,
                  py: 1,
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1,
                  flexShrink: 0,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                }}
              >
                {activeFilterEntries.map(({ key, filter }) => (
                  <Chip
                    key={key}
                    size='small'
                    label={getFilterLabel(key, filter)}
                    onDelete={() => clearColumnFilter(key)}
                  />
                ))}
                <Chip
                  size='small'
                  icon={<ClearIcon />}
                  label='Alle löschen'
                  onClick={clearAllColumnFilters}
                  variant='outlined'
                />
              </Box>
            ) : (
              <>
                {isMidDesktop ? (
                  <Box p={2} display='flex' flexDirection='column' gap={2} sx={{ flexShrink: 0 }}>
                    {hasControlsRow && (
                      <Box display='flex' alignItems='flex-start' flexWrap='wrap' gap={2}>
                        {TopControlsBase}
                      </Box>
                    )}
                    {activeFilterEntries.length > 0 && (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {activeFilterEntries.map(({ key, filter }) => (
                          <Chip
                            key={key}
                            size='small'
                            label={getFilterLabel(key, filter)}
                            onDelete={() => clearColumnFilter(key)}
                          />
                        ))}
                        <Chip
                          size='small'
                          icon={<ClearIcon />}
                          label='Alle löschen'
                          onClick={clearAllColumnFilters}
                          variant='outlined'
                        />
                      </Box>
                    )}
                  </Box>
                ) : (
                  <Box p={2} display='flex' alignItems='flex-start' flexWrap='wrap' gap={2} sx={{ flexShrink: 0 }}>
                    {TopControlsBase}
                  </Box>
                )}
                {!isMidDesktop && activeFilterEntries.length > 0 && (
                  <Box
                    sx={{ px: 2, pb: 2, display: 'flex', flexWrap: 'wrap', gap: 1, flexShrink: 0 }}
                  >
                    {activeFilterEntries.map(({ key, filter }) => (
                      <Chip
                        key={key}
                        size='small'
                        label={getFilterLabel(key, filter)}
                        onDelete={() => clearColumnFilter(key)}
                      />
                    ))}
                    <Chip
                      size='small'
                      icon={<ClearIcon />}
                      label='Alle löschen'
                      onClick={clearAllColumnFilters}
                      variant='outlined'
                    />
                  </Box>
                )}
              </>
            )}
          </>
        )}
        {items === null || (isLoading && sortedItems.length === 0) ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              p: 4,
              minHeight: 200,
            }}
          >
            <CircularProgress color='primary' />
          </Box>
        ) : sortedItems.length > 0 ? (
          <TableContainer
            sx={{
              flexGrow: 1,
              overflow: 'auto',
              minHeight: 0,
              ...(isLoading ? { opacity: 0.55, pointerEvents: 'none' } : {}),
            }}
          >
            <Table stickyHeader sx={{ minWidth: 650 }} aria-label={title}>
            <TableHead>
              <TableRow>
                {selectionEnabled && (
                  <TableCell
                    padding='checkbox'
                    sx={{
                      ...stickyHeaderCellBaseSx,
                      left: stickyLeftConfig.selectionLeft,
                      width: stickyLeftConfig.selectionWidth,
                      minWidth: stickyLeftConfig.selectionWidth,
                      maxWidth: stickyLeftConfig.selectionWidth,
                    }}
                  >
                    <Checkbox
                      indeterminate={selectionHeaderState.indeterminate}
                      checked={selectionHeaderState.checked}
                      onChange={e => toggleSelectionAllVisible(sortedItems, e.target.checked)}
                    />
                  </TableCell>
                )}
                {!isSingleObjectListContext && canCreate && (
                  <TableCell
                    padding='checkbox'
                    sx={{
                      ...stickyHeaderCellBaseSx,
                      left: stickyLeftConfig.createLeft,
                      width: stickyLeftConfig.createWidth,
                      minWidth: stickyLeftConfig.createWidth,
                      maxWidth: stickyLeftConfig.createWidth,
                    }}
                  >
                    {headerAddTool}
                  </TableCell>
                )}
                {detailViewEnabled && (
                  <TableCell
                    padding='checkbox'
                    sx={{
                      ...stickyHeaderCellBaseSx,
                      left: stickyLeftConfig.detailOpenLeft,
                      width: stickyLeftConfig.detailOpenWidth,
                      minWidth: stickyLeftConfig.detailOpenWidth,
                      maxWidth: stickyLeftConfig.detailOpenWidth,
                    }}
                  >
                    {' '}
                  </TableCell>
                )}
                {hasLeadingRowAction && (
                  <TableCell
                    padding='checkbox'
                    sx={{
                      ...stickyHeaderCellBaseSx,
                      left: stickyLeftConfig.leadingActionLeft,
                      width: stickyLeftConfig.leadingActionWidth,
                      minWidth: stickyLeftConfig.leadingActionWidth,
                      maxWidth: stickyLeftConfig.leadingActionWidth,
                    }}
                  >
                    {/* Intentionally left blank to keep the column minimal */}
                    {' '}
                  </TableCell>
                )}
                {orderedVisibleColumns.map(key => (
                  <TableCell
                    key={key}
                    sortDirection={sortConfig.key === key ? sortConfig.direction : false}
                    sx={{
                      whiteSpace: 'nowrap',
                      ...(key === firstOrderedVisibleColumnKey
                        ? {
                            ...stickyHeaderCellBaseSx,
                            left: stickyLeftConfig.firstDataLeft,
                            zIndex: nested ? 3 : 25,
                          }
                        : stickyHeaderCellSx),
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        justifyContent: 'space-between',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <TableSortLabel
                          active={sortConfig.key === key}
                          direction={sortConfig.key === key ? sortConfig.direction : 'asc'}
                          onClick={e => {
                            e.stopPropagation();
                            handleRequestSort(key);
                          }}
                        >
                          {key === ID_COLUMN_KEY ? ID_COLUMN_KEY : (schema[key]?.label || key)}
                        </TableSortLabel>
                        {(() => {
                          const fieldSchema = schema?.[key];
                          const extra = fieldSchema?.headerExtra;
                          if (!extra) return null;
                          const node =
                            typeof extra === 'function' ? extra({ key, fieldSchema }) : extra;
                          if (!node) return null;
                          return (
                            <Box
                              sx={{ display: 'flex', alignItems: 'center', ml: 0.5 }}
                              onClick={e => e.stopPropagation()}
                              onMouseDown={e => e.stopPropagation()}
                            >
                              {node}
                            </Box>
                          );
                        })()}
                        {isFilterableColumn(key) && (
                          <IconButton
                            size='small'
                            onClick={e => {
                              e.stopPropagation();
                              openFilterMenu(key, e);
                            }}
                            color={columnFilters?.[key]?.op ? 'primary' : 'default'}
                            sx={{ ml: 0.5 }}
                          >
                            <FilterAltIcon fontSize='small' />
                          </IconButton>
                        )}
                      </Box>
                      {!showTrailingActions && key === lastOrderedVisibleColumnKey && headerColumnTool}
                    </Box>
                  </TableCell>
                ))}
                {showTrailingActions && (
                  <TableCell align='right' sx={stickyHeaderCellSx}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                      }}
                    >
                      <Typography variant='body2' sx={{ fontWeight: 600 }}>
                        Aktionen
                      </Typography>
                      {headerColumnTool}
                    </Box>
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>{tableRows}</TableBody>
          </Table>
          </TableContainer>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              p: 4,
              minHeight: 200,
              gap: 2,
            }}
          >
            <PlaylistRemoveIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
            <Typography variant='body1' color='text.secondary'>
              {currentSearchTerm
                ? 'Keine Einträge für diese Suche gefunden.'
                : 'Keine Einträge vorhanden.'}
            </Typography>
            {!isSingleObjectListContext && canCreate && (
              <Button
                variant='contained'
                color='success'
                size='small'
                startIcon={<AddIcon />}
                onClick={handleOpenDialog}
                sx={{ whiteSpace: 'nowrap' }}
              >
                <LockIconWrapper right={`${rightsResourceType}:create`}>{addLabel}</LockIconWrapper>
              </Button>
            )}
          </Box>
        )}

        {(() => {
          const model = paginationModel;

          if (!model) return null;
          const totalPages = model.limit > 0 ? Math.ceil(model.total / model.limit) : 0;
          const shouldShow = totalPages > 1;
          if (!shouldShow) return null;

          return (
            <Box sx={{ borderTop: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
              <TablePagination
                component='div'
                count={model.total}
                page={Math.max(0, model.page - 1)}
                onPageChange={(e, p) => model.onChange({ page: p + 1, limit: model.limit })}
                rowsPerPage={model.limit}
                onRowsPerPageChange={e => {
                  const next = Number.parseInt(e.target.value, 10);
                  if (!Number.isFinite(next) || next <= 0) return;
                  model.onChange({ page: 1, limit: next });
                }}
                rowsPerPageOptions={model.rowsPerPageOptions || [10, 20, 25, 50, 100]}
                labelRowsPerPage='Einträge:'
                labelDisplayedRows={({ from, to, count }) =>
                  model.totalIsCapped
                    ? `${from}-${to} von mehr als ${count}`
                    : `${from}-${to} von ${count}`
                }
              />
            </Box>
          );
        })()}
      </Paper>
    );
  }, [
    filteredItems,
    columnFilteredItems,
    sortedItems,
    schema,
    expandedItems,
    canCreate,
    canUpdate,
    canDelete,
    canDuplicate,
    hasLeadingRowAction,
    leadingRowAction,
    detailViewEnabled,
    showTrailingActions,
    handleItemSelect,
    handleDeleteClick,
    handleDuplicateItem,
    nested,
    title,
    tableRows,
    handleOpenDialog,
    visibleColumns,
    columnOptions,
    pagination,
    isLoading,
    currentSearchTerm,
    isCardView,
    isXsDown,
    isLargeDesktop,
    isMidDesktop,
    mobileNestedContext,
    items,
    sortConfig.key,
    sortConfig.direction,
    handleRequestSort,
    activeColumnFilterCount,
    clearAllColumnFilters,
    clearColumnFilter,
    activeFilterEntries,
    getFilterLabel,
    isFilterableColumn,
    openFilterMenu,
    columnFilters,
    renderTopControlsStart,
    renderTopControlsEnd,
    onRowClick,
    selectionEnabled,
    selectionSelectedIds,
    getSelectionRowId,
    toggleSelectionAllVisible,
    toggleSelectionRow,
    externalPagination,
    hideRowActionsColumn,
    stickyLeftConfig,
    stickyHeaderCellBaseSx,
    stickyHeaderCellSx,
    cardVisibleItems,
    mobilePreviewColumns,
    mobileCollapsibleColumns,
    expandedCards,
    toggleCardExpand,
    paginationModel,
    cardClientPaginationActive,
    effectiveCardPage,
    cardPageSize,
    enterMobileNested,
    exitMobileNested,
    renderField,
    renderNestedContent,
    renderObjectContent,
    addNestedItemToParent,
    updateNestedItemInParent,
    deleteNestedItemFromParent,
    resourceType,
    nestedBasePath,
  ]);

  if (!schema || Object.keys(schema).length === 0) {
    return <Typography>No schema defined</Typography>;
  }

  return (
    <Box ref={rootRef} sx={viewRootTextPrimarySx}>
      {mainListContent}
      <Menu
        anchorEl={columnMenuAnchorEl}
        open={Boolean(columnMenuAnchorEl)}
        onClose={closeColumnMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        {columnOptions.map(option => (
          <MenuItem
            key={option.value}
            onClick={e => {
              e.stopPropagation();
              toggleVisibleColumn(option.value);
            }}
          >
            <Checkbox checked={visibleColumns.indexOf(option.value) > -1} />
            <ListItemText primary={option.label} />
          </MenuItem>
        ))}
      </Menu>
      <Menu
        anchorEl={filterMenu.anchorEl}
        open={Boolean(filterMenu.anchorEl)}
        onClose={closeFilterMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        {(() => {
          const key = filterMenu.key;
          if (!key) return null;

          const fieldSchema = schema?.[key] || {};
          const type = getFilterableType(fieldSchema);
          if (!type) return null;

          const op =
            filterMenu.draftOp ||
            (type === 'boolean'
              ? 'eq'
              : type === 'select' || type === 'multi-select'
                ? 'eq'
                : type === 'string'
                  ? 'contains'
                : type === 'presence'
                  ? 'present'
                  : 'gt');
          const value = filterMenu.draftValue ?? '';
          const isSelectType = type === 'select' || type === 'multi-select';
          const selectOptionsSource = fieldSchema.api?.resource
            ? memoizedOptions?.[key]
            : fieldSchema.options;
          const selectOptions = Array.isArray(selectOptionsSource) ? selectOptionsSource : [];
          const selectedSelectValue = isSelectType
            ? type === 'multi-select'
              ? selectOptions.filter(opt =>
                  (Array.isArray(value) ? value : value ? [value] : []).some(
                    v => String(v) === String(opt?.value)
                  )
                )
              : selectOptions.find(opt => String(opt?.value) === String(value)) || null
            : null;

          const setOp = nextOp => {
            setFilterMenu(prev => (prev?.key === key ? { ...prev, draftOp: nextOp } : prev));
          };

          const setValue = nextValue => {
            setFilterMenu(prev => (prev?.key === key ? { ...prev, draftValue: nextValue } : prev));
          };

          const commitFilter = (nextOp, nextValue) => {
            if (nextOp !== 'present' && nextOp !== 'not_present') {
              const isEmptyValue = Array.isArray(nextValue)
                ? nextValue.length === 0
                : String(nextValue ?? '').trim().length === 0;
              if (isEmptyValue) {
                clearColumnFilter(key);
                return;
              }
            }

            setColumnFilters(prev => ({
              ...(prev || {}),
              [key]: { op: nextOp, value: nextValue },
            }));
          };

          const applyFromInputRef = () => {
            const raw = filterValueInputRef.current?.value ?? '';
            setValue(raw);
            commitFilter(op, raw);
            closeFilterMenu();
          };

          const canUseValueInput = !isSelectType && (type === 'number' || type === 'date' || type === 'string');
          const inputType = type === 'number' ? 'number' : type === 'date' ? 'date' : 'text';

          return (
            <Box
              sx={{ p: 2, minWidth: 260, maxWidth: 340 }}
              onClick={e => e.stopPropagation()}
              onKeyDown={e => {
                if (e.key !== 'Enter') return;
                if (type !== 'number' && type !== 'date' && type !== 'string') return;
                if (op === 'present' || op === 'not_present') return;
                e.preventDefault();
                applyFromInputRef();
              }}
            >
              <Typography variant='subtitle2' sx={{ mb: 1 }}>
                {schema[key]?.label || key}
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
                  {(type === 'number' || type === 'date') && (
                    <>
                      <MenuItem selected={op === 'gt'} onClick={() => setOp('gt')}>größer als</MenuItem>
                      <MenuItem selected={op === 'gte'} onClick={() => setOp('gte')}>größer/gleich</MenuItem>
                      <MenuItem selected={op === 'lt'} onClick={() => setOp('lt')}>kleiner als</MenuItem>
                      <MenuItem selected={op === 'lte'} onClick={() => setOp('lte')}>kleiner/gleich</MenuItem>
                      <MenuItem selected={op === 'eq'} onClick={() => setOp('eq')}>gleich</MenuItem>
                    </>
                  )}

                  {type === 'string' && (
                    <>
                      <MenuItem selected={op === 'contains'} onClick={() => setOp('contains')}>
                        enthält
                      </MenuItem>
                      <MenuItem selected={op === 'starts_with'} onClick={() => setOp('starts_with')}>
                        beginnt mit
                      </MenuItem>
                      <MenuItem selected={op === 'ends_with'} onClick={() => setOp('ends_with')}>
                        endet mit
                      </MenuItem>
                      <MenuItem selected={op === 'eq'} onClick={() => setOp('eq')}>
                        gleich
                      </MenuItem>
                    </>
                  )}

                  {type === 'boolean' && (
                    <>
                      <MenuItem selected={op === 'eq'} onClick={() => setOp('eq')}>gleich</MenuItem>
                      {op === 'eq' && (
                        <>
                          <MenuItem
                            selected={value === true || value === 'true'}
                            onClick={() => {
                              setValue(true);
                              commitFilter('eq', true);
                              closeFilterMenu();
                            }}
                            sx={{ pl: 4 }}
                          >
                            wahr
                          </MenuItem>
                          <MenuItem
                            selected={value === false || value === 'false' || value === ''}
                            onClick={() => {
                              setValue(false);
                              commitFilter('eq', false);
                              closeFilterMenu();
                            }}
                            sx={{ pl: 4 }}
                          >
                            falsch
                          </MenuItem>
                        </>
                      )}
                    </>
                  )}

                    {isSelectType && (
                      <>
                        <MenuItem selected={op === 'eq'} onClick={() => setOp('eq')}>
                          gleich
                        </MenuItem>
                      </>
                    )}

                  <MenuItem
                    selected={op === 'present'}
                    onClick={() => {
                      setOp('present');
                      setValue('');
                      commitFilter('present', '');
                      closeFilterMenu();
                    }}
                  >
                    vorhanden
                  </MenuItem>
                  <MenuItem
                    selected={op === 'not_present'}
                    onClick={() => {
                      setOp('not_present');
                      setValue('');
                      commitFilter('not_present', '');
                      closeFilterMenu();
                    }}
                  >
                    nicht vorhanden
                  </MenuItem>
                </Box>

                  {isSelectType && op !== 'present' && op !== 'not_present' && (
                    <Autocomplete
                      size='small'
                      multiple={type === 'multi-select'}
                      options={selectOptions}
                      value={selectedSelectValue}
                      isOptionEqualToValue={(a, b) => String(a?.value) === String(b?.value)}
                      getOptionLabel={opt => (opt?.label ? String(opt.label) : String(opt?.value ?? ''))}
                      onChange={(e, next) => {
                        e?.stopPropagation?.();
                        if (type === 'multi-select') {
                          const nextValues = (Array.isArray(next) ? next : [])
                            .map(opt => opt?.value)
                            .filter(v => v !== null && v !== undefined && String(v).trim() !== '');
                          setValue(nextValues);
                          commitFilter('eq', nextValues);
                          return;
                        }
                        const nextValue = next ? next.value : '';
                        setValue(nextValue);
                        commitFilter('eq', nextValue);
                        closeFilterMenu();
                      }}
                      renderInput={params => (
                        <TextField
                          {...params}
                          autoFocus
                          label='Suchen'
                          placeholder='Tippen zum Filtern…'
                          onClick={ev => ev.stopPropagation()}
                          onKeyDown={ev => ev.stopPropagation()}
                        />
                      )}
                    />
                  )}

                {canUseValueInput && op !== 'present' && op !== 'not_present' && (
                  <TextField
                    key={`${key}-${op}`}
                    inputRef={filterValueInputRef}
                    autoFocus
                    size='small'
                    type={inputType}
                    defaultValue={value}
                    onKeyDown={e => {
                      e.stopPropagation();
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        applyFromInputRef();
                      }
                    }}
                  />
                )}

                {(type === 'number' || type === 'date' || type === 'string') &&
                  op !== 'present' &&
                  op !== 'not_present' && (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <Button variant='contained' size='small' onClick={applyFromInputRef}>
                        Anwenden
                      </Button>
                    </Box>
                  )}
              </Box>
            </Box>
          );
        })()}
      </Menu>
      <AddItemDialog
        open={isDialogOpen}
        onClose={handleCloseDialog}
        onAdd={handleAddItem}
        schema={schema}
        apiOptions={filteredApiOptions}
        title={title}
        resourceType={resourceType}
      />

      {isDetailViewOpen && (
        <EditableDetailView
          open={isDetailViewOpen}
          onClose={handleDetailViewClose}
          item={selectedItem}
          schema={detailViewSchema}
          onItemDelete={deleteItem}
          title={`${selectedItem?.username || selectedItem?.name || 'Details'}`}
          apiOptions={filteredApiOptions}
          onFieldUpdate={handleFieldUpdate}
          customRenderers={options?.customRenderers}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmation.open} onClose={handleDeleteCancel}>
        <DialogTitle>Löschen bestätigen</DialogTitle>
        <DialogContent>
          <Typography>
            Möchtest du {deleteConfirmation.itemName} wirklich löschen? Diese Aktion kann nicht
            rückgängig gemacht werden.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={handleDeleteCancel} variant='contained' color='success' size='large'>
            Abbrechen
          </Button>
          <Button onClick={handleDeleteConfirm} variant='contained' color='error' size='large'>
            <LockIconWrapper right={`${rightsResourceType}:delete`}>Löschen</LockIconWrapper>
          </Button>
        </DialogActions>
      </Dialog>

      {/* Password Dialog */}
      <PasswordDialog
        open={passwordDialog.open}
        onClose={handlePasswordDialogClose}
        onSave={handlePasswordSave}
      />

      {/* Empty Nested Dialog */}
      <AddItemDialog
        open={emptyNestedDialog.open}
        onClose={handleEmptyNestedDialogClose}
        onAdd={handleEmptyNestedAdd}
        schema={emptyNestedDialog.schema || {}}
        apiOptions={emptyNestedApiOptions}
        title={
          schema[emptyNestedDialog.path]?.addButtonText ||
          `${schema[emptyNestedDialog.path]?.label || emptyNestedDialog.path}`
        }
        resourceType={resourceType}
      />
    </Box>
  );
}

export default memo(EditableList);
